import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import VideoCameraFrontRoundedIcon from '@mui/icons-material/VideoCameraFrontRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded'
import HeadphonesRoundedIcon from '@mui/icons-material/HeadphonesRounded'
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded'
import MicRoundedIcon from '@mui/icons-material/MicRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded'
import PodcastsRoundedIcon from '@mui/icons-material/PodcastsRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded'
import { useAuth } from '../../context/useAuth'
import { useChat } from '../../context/useChat'
import { useTimeline } from '../../context/useTimeline'
import { useToast } from '../../context/useToast'
import { useOrbit } from '../Common/Orbit/useOrbit'
import { buildActionItemSlices, buildAgendaItemSlices, buildAttendeeChipSlices, buildMeetingCardSlices, buildMeetingsCanvasSlices } from './MeetingLayout.orbit'
import { withLcmChip } from '../Common/Orbit/lcmOrbitSlices'
import { useLcmChipDispatchers } from '../Common/Orbit/useLcmChipDispatchers'
import type { OrbitActionBridge } from '../Common/Orbit/orbitSliceHelpers'
import { resolveOrbitActionId } from '../Common/Orbit/orbitSliceIdMap'
import { invokeAction } from '../../services/actions/registry'
import type { MeetingActionOps } from '../../services/actions/action'
import { useWorkspace } from '../../context/useWorkspace'
import { roleCanPerform } from '../../utils/workspacePermissions'
import { formatRoleLabel, roleLabelKey } from '../../utils/roleLabels'
import { useEdgeFadeScroll } from '../../hooks/useEdgeFadeScroll'
import { useIntegrationGateway } from '../../hooks/useCheckConnection'
import { useDesignAssets } from '../../hooks/useDesignAssets'
import { useFormDefinitions } from '../../hooks/useFormDefinitions'
import { useNls } from '../../nls/nlsProvider'
import { bulkPushMeetingsToGoogleCalendar, cancelMeeting, createMeetingProviderAdapter, deleteMeeting, meetingStatusLabel, requestMeetingTranscription, resyncMeetingCalendar, subscribeGoogleCalendarBusySlots, subscribeMeetingMembers, subscribeMeetings, updateMeetingAgenda, updateMeetingAttachments, updateMeetingDetails, updateMeetingRsvp, type MeetingBusySlot, type MeetingMember } from '../../services/meetingService'
import { subscribeHuddleSessions, type HuddleSession } from '../../services/huddleSessionService'
import { subscribeWorkspaceFiles, uploadWorkspaceArtifact } from '../../services/storageHubService'
import type { WorkspaceFileMetadata } from '../../types/schema'
import { getDemoWorkspaceData } from '../../explore_seeds/ariaAndCoDemoData'
import type { MeetingAgendaNode, MeetingAttachment, MeetingAttachmentEntity, MeetingCreateInput, MeetingProvider, MeetingRecord, MeetingRsvpStatus } from '../../types/meetings'
import { getAgendaNodeReadiness } from '../../utils/readinessHelper'
import { useMeetingReadiness } from '../../hooks/useMeetingReadiness'
import { GhostBadge } from '../ui/GhostBadge'
import { MeetingReadinessGate } from './MeetingReadinessGate'
import { meetingScopeNodeIdPatch } from './meetingScopePatch'
import { Button, Collapse, HiddenFileInput, LinkedGoalPicker, MeetingWhenPicker, PendingChangesDock, RailChipTabs, RailPageShell, Select, TagChip, TextInput, Textarea, WorkspaceItemPicker, useLinkedGoalPrefill } from '../Common'
import type { PendingChangesDockAction, RailChipTabItem, WorkspaceItemReference } from '../Common'
import { SheetSelect } from '../Common/SheetSelect'
import { FloatingMenu } from '../Common/FloatingMenu'
import { AppDialog } from '../Common/AppDialog'
import { BottomSheet } from '../Common/BottomSheet'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import {
  formatMeetingCount,
  getMeetingDirtyDialog,
  getMeetingSectionLabels,
  getMeetingSectionMeta,
  getMeetingViewLabels,
} from './MeetingsNls'
import {
  DRAFT_MEETING_DROP_TARGET_ID,
  getEffectiveDraftParticipantIds,
  getEffectiveParticipantIds as getEffectiveParticipantIdsPure,
  getPendingParticipantIds as getPendingParticipantIdsPure,
  isPhantomDraftAvatarFor,
  participantRsvpStatus,
  rosterClickTargetId,
  shouldApplyParticipantDropToCurrentDraft,
} from './meetingParticipants'
import { ExternalLinkSidecard } from '../LinkHub/shared/ExternalLinkSidecard'
import { bucketScheduleEntriesByDay, buildMeetingScheduleEntries, layoutScheduleEntriesForWeek } from './meetingCalendarPresentation'
import './meetings.css'

type ViewMode = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR'
type ScheduleView = 'MONTH' | 'WEEK'
type SectionMode = 'SCHEDULE' | 'HUDDLE' | 'MEETING'
type NotifyMode = 'ALL' | 'CHANGED' | 'NONE'
type WeekDraftSelection = { dayIdx: number; startMin: number; endMin: number } | null

const WEEK_SLOT_MINUTES = 30
const WEEK_SLOT_HEIGHT_PX = 24
const WEEK_TOTAL_MINUTES = 24 * 60
const WEEK_TOTAL_HEIGHT_PX = (WEEK_TOTAL_MINUTES / WEEK_SLOT_MINUTES) * WEEK_SLOT_HEIGHT_PX
export type MeetingDraft = {
  id: string | null
  title: string
  description: string
  sourceProvider: MeetingProvider
  startInput: string
  durationMinutes: number
  participantIds: string[]
  chatChannelIds: string[]
  agendaMarkdown: string
  agendaNodes: MeetingAgendaNode[]
  scopeNodeId?: string | null
}
type DraftSelection = { left: number; width: number } | null

const SECTION_META: Array<{ id: SectionMode; icon: ReactNode }> = [
  { id: 'SCHEDULE', icon: <CalendarMonthRoundedIcon /> },
  { id: 'HUDDLE', icon: <GraphicEqRoundedIcon /> },
  { id: 'MEETING', icon: <PodcastsRoundedIcon /> },
]
const COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)', 'var(--chart-6)', 'var(--chart-7)', 'var(--chart-8)']
const DAY_MS = 24 * 60 * 60 * 1000
const MIN_DURATION = 15
const MAX_DURATION = 7 * 24 * 60
const HUDDLE_VISIBILITY_STORAGE_KEY = 'timelining.meetings.showHuddles'

const pad = (value: number) => String(value).padStart(2, '0')
const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())
const addDays = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
const addMonths = (date: Date, months: number) => new Date(date.getFullYear(), date.getMonth() + months, 1)
const meetingDate = (value: MeetingRecord['scheduledAt']) => value instanceof Date ? value : 'toDate' in value ? value.toDate() : new Date(value as unknown as string)
const toLocalInput = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
const inputToDate = (value: string) => new Date(value)
const clampDuration = (minutes: number) => Math.max(MIN_DURATION, Math.min(MAX_DURATION, Math.round(minutes || 30)))
const participantIds = (meeting: MeetingRecord | null) => (meeting?.participants ?? []).flatMap((participant) => participant.external ? [] : [participant.userId])
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'M'
const snapDateToMinutes = (date: Date, intervalMinutes = 15) => {
  const next = new Date(date)
  const intervalMs = intervalMinutes * 60_000
  next.setTime(Math.round(next.getTime() / intervalMs) * intervalMs)
  next.setSeconds(0, 0)
  return next
}

const readHuddleVisibility = () => {
  try {
    return window.localStorage.getItem(HUDDLE_VISIBILITY_STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

const draftFromMeeting = (meeting: MeetingRecord | null, fallbackProvider: MeetingProvider = 'GOOGLE_MEET', defaultScopeNodeId: string | null = null): MeetingDraft => {
  if (!meeting) {
    const start = new Date()
    start.setMinutes(Math.ceil(start.getMinutes() / 30) * 30, 0, 0)
    return { id: null, title: '', description: '', sourceProvider: fallbackProvider, startInput: toLocalInput(start), durationMinutes: 30, participantIds: [], chatChannelIds: [], agendaMarkdown: '# Shared agenda\n- Opening context\n- Decisions\n- Next actions', agendaNodes: [], scopeNodeId: defaultScopeNodeId }
  }
  return {
    id: meeting.id,
    title: meeting.title,
    description: meeting.description ?? '',
    sourceProvider: meeting.sourceProvider,
    startInput: toLocalInput(meetingDate(meeting.scheduledAt)),
    durationMinutes: meeting.durationMinutes ?? 30,
    participantIds: participantIds(meeting),
    chatChannelIds: meeting.chatChannelIds ?? (meeting.chatChannelId ? [meeting.chatChannelId] : []),
    agendaMarkdown: meeting.agendaMarkdown ?? '',
    agendaNodes: meeting.agendaNodes ?? [],
    scopeNodeId: meeting.scopeNodeId ?? null,
  }
}

export interface MeetingOpsDeps {
  openScheduleModal?: () => void
  setSelectedId?: (id: string | null) => void
  deleteMeeting?: (id: string) => Promise<unknown> | unknown
  showToast?: (message: string, tone?: 'info' | 'success' | 'warning' | 'error') => void
  hasEditableDraft?: boolean
  draft?: MeetingDraft | null
  getDraft?: () => MeetingDraft | null
  setDraft?: React.Dispatch<React.SetStateAction<MeetingDraft>>
}

// eslint-disable-next-line react-refresh/only-export-components
export const executeAttachUpload = async ({
  file,
  workspaceId,
  meetingId,
  userId,
  attachments,
  uploadArtifact,
  commitAttachments,
  showToast,
}: {
  file: File
  workspaceId: string
  meetingId: string
  userId: string
  attachments: MeetingAttachment[]
  uploadArtifact: typeof uploadWorkspaceArtifact
  commitAttachments: (next: MeetingAttachment[]) => Promise<boolean>
  showToast: (message: string, type?: 'info' | 'success' | 'error' | 'warning') => void
}) => {
  try {
    const result = await uploadArtifact({
      workspaceId,
      file,
      artifactType: 'MEETING_DOCUMENT',
      artifactId: meetingId,
    })
    const next: MeetingAttachment[] = [
      ...attachments,
      { kind: 'UPLOAD', fileId: result.fileId, fileName: file.name, addedAt: new Date().toISOString(), addedBy: userId },
    ]
    const committed = await commitAttachments(next)
    if (!committed) return
    showToast(`Attached “${file.name}” to the meeting.`, 'success')
  } catch (error) {
    showToast(error instanceof Error ? error.message : 'Upload failed.', 'error')
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export const isAttachableMeetingRefKind = (
  kind: WorkspaceItemReference['kind'],
): kind is MeetingAttachmentEntity['refKind'] => {
  return (
    kind === 'goal' ||
    kind === 'task' ||
    kind === 'subtask' ||
    kind === 'checklist' ||
    kind === 'form' ||
    kind === 'design' ||
    kind === 'upload' ||
    kind === 'link'
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const executeAttachEntity = async ({
  reference,
  userId,
  attachments,
  commitAttachments,
  showToast,
}: {
  reference: WorkspaceItemReference
  userId: string
  attachments: MeetingAttachment[]
  commitAttachments: (next: MeetingAttachment[]) => Promise<boolean>
  showToast: (message: string, type?: 'info' | 'success' | 'error' | 'warning') => void
}): Promise<boolean> => {
  if (!isAttachableMeetingRefKind(reference.kind)) {
    return false
  }
  const isAlreadyAttached = attachments.some(
    (entry) => entry.kind === 'ENTITY' && entry.refId === reference.id,
  )
  if (isAlreadyAttached) {
    showToast('That item is already attached.', 'info')
    return false
  }
  const next: MeetingAttachment[] = [
    ...attachments,
    {
      kind: 'ENTITY',
      refId: reference.id,
      refKind: reference.kind,
      refLabel: reference.label,
      parentId: reference.parentId,
      addedAt: new Date().toISOString(),
      addedBy: userId,
    },
  ]
  return await commitAttachments(next)
}

// eslint-disable-next-line react-refresh/only-export-components
export const createMeetingOps = ({
  openScheduleModal,
  setSelectedId,
  deleteMeeting: deleteMeetingFn,
  showToast,
  hasEditableDraft,
  draft,
  getDraft,
  setDraft,
}: MeetingOpsDeps): MeetingActionOps => ({
  createMeeting: () => {
    openScheduleModal?.()
  },
  openMeeting: (id: string) => {
    setSelectedId?.(id)
  },
  deleteMeeting: async (id: string) => {
    await deleteMeetingFn?.(id)
  },
  rescheduleMeeting: (id: string) => {
    setSelectedId?.(id)
    openScheduleModal?.()
  },
  removeAttendee: async (attendeeId: string) => {
    const currentDraft = getDraft ? getDraft() : draft ?? null
    if (!hasEditableDraft || !currentDraft || !currentDraft.participantIds.includes(attendeeId)) {
      throw new Error('Remove attendee is not available here')
    }
    setDraft?.((prev) => ({
      ...prev,
      participantIds: prev.participantIds.filter((id) => id !== attendeeId),
    }))
    showToast?.('Member removed. Save to send updates.', 'info')
  },
  deleteAgendaItem: async () => {
    throw new Error('Agenda item delete is not available yet')
  },
  deleteActionItem: async () => {
    throw new Error('Action item delete is not available yet')
  },
})

const sameDraft = (draft: MeetingDraft, meeting: MeetingRecord | null) => {
  if (!meeting) return !draft.title && draft.participantIds.length === 0
  const sorted = (ids: string[]) => [...ids].sort().join('|')
  return draft.title === meeting.title
    && draft.description === (meeting.description ?? '')
    && inputToDate(draft.startInput).getTime() === meetingDate(meeting.scheduledAt).getTime()
    && draft.durationMinutes === (meeting.durationMinutes ?? 30)
    && sorted(draft.participantIds) === sorted(participantIds(meeting))
    && sorted(draft.chatChannelIds) === sorted(meeting.chatChannelIds ?? (meeting.chatChannelId ? [meeting.chatChannelId] : []))
    && draft.agendaMarkdown === (meeting.agendaMarkdown ?? '')
}

const getRange = (view: ViewMode, focusedDate: Date) => {
  const day = startOfDay(focusedDate)
  if (view === 'DAY') return { start: day, end: addDays(day, 1) }
  if (view === 'WEEK') {
    const mondayOffset = (day.getDay() + 6) % 7
    const start = addDays(day, -mondayOffset)
    return { start, end: addDays(start, 7) }
  }
  if (view === 'MONTH') {
    const start = new Date(day.getFullYear(), day.getMonth(), 1)
    return { start, end: new Date(day.getFullYear(), day.getMonth() + 1, 1) }
  }
  const start = new Date(day.getFullYear(), 0, 1)
  return { start, end: new Date(day.getFullYear() + 1, 0, 1) }
}

const rangeLabels = (view: ViewMode, start: Date, end: Date, locale: string) => {
  if (view === 'DAY') return Array.from({ length: 7 }, (_, index) => {
    const hour = index * 4
    return { key: `h-${hour}`, label: `${pad(hour)}:00`, sub: '', left: (hour / 24) * 100 }
  })
  if (view === 'YEAR') return Array.from({ length: 12 }, (_, index) => {
    const cursor = addMonths(start, index)
    return { key: cursor.toISOString(), label: cursor.toLocaleDateString(locale, { month: 'short' }), sub: '', left: ((cursor.getTime() - start.getTime()) / (end.getTime() - start.getTime())) * 100 }
  })
  const count = Math.round((end.getTime() - start.getTime()) / DAY_MS)
  return Array.from({ length: count }, (_, index) => {
    const cursor = addDays(start, index)
    return { key: cursor.toISOString(), label: String(cursor.getDate()), sub: cursor.toLocaleDateString(locale, { weekday: 'short' }), left: (index / count) * 100 }
  })
}

const pickLiveMeetingId = (list: MeetingRecord[]) => {
  const now = Date.now()
  for (const meeting of list) {
    if (meeting.status === 'CANCELLED') continue
    const begins = meetingDate(meeting.scheduledAt).getTime()
    const finishes = begins + (meeting.durationMinutes ?? 30) * 60_000
    if (begins <= now && now < finishes) return meeting.id
  }
  return null
}

const layoutMeetings = (meetings: MeetingRecord[], start: Date, end: Date) => {
  const rangeStart = start.getTime()
  const rangeEnd = end.getTime()
  const span = rangeEnd - rangeStart
  const lanes: number[] = []
  return meetings
    .map((meeting, index) => {
      const begins = meetingDate(meeting.scheduledAt).getTime()
      const finishes = begins + (meeting.durationMinutes ?? 30) * 60_000
      if (finishes <= rangeStart || begins >= rangeEnd) return null
      const clampedStart = Math.max(begins, rangeStart)
      const clampedEnd = Math.min(finishes, rangeEnd)
      let lane = lanes.findIndex((laneEnd) => laneEnd <= clampedStart)
      if (lane === -1) { lane = lanes.length; lanes.push(clampedEnd) } else lanes[lane] = clampedEnd
      return {
        meeting,
        lane,
        left: ((clampedStart - rangeStart) / span) * 100,
        width: Math.max(5, ((clampedEnd - clampedStart) / span) * 100),
        color: COLORS[index % COLORS.length],
      }
    })
    .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
}

export const MeetingLayout = ({
  initialMeetingId = null,
  onOpenChat,
  onOpenDocs,
  onOpenLink,
  allowScheduling,
  allowManaging,
  allowJoining,
  variant = 'desktop',
  isMobile = false,
}: {
  initialMeetingId?: string | null
  onOpenChat?: (meeting: MeetingRecord) => void
  onOpenDocs: (meetingId: string) => void
  onOpenLink?: () => void
  allowScheduling?: boolean
  allowManaging?: boolean
  allowJoining?: boolean
  variant?: 'desktop' | 'member'
  isMobile?: boolean
}) => {
  const { user } = useAuth()
  const { activeChannelId, channelsList, huddlePresence, setActiveChannelId, setHuddleState, createChannel, requestCreateChannelDraft } = useChat()
  const { activeWorkspaceId, activeRole, appMode, workspaces, canPerform } = useWorkspace()
  const { elements, checklistItems, isAccessRestricted } = useTimeline()
  const { showToast } = useToast()
  const lcmDispatchers = useLcmChipDispatchers()
  const meetingOpsRef = useRef<MeetingActionOps>({})
  const onOrbitAction = useCallback<OrbitActionBridge>((sliceId, label, id, title, phase6) => {
    if (phase6) {
      showToast('Coming in Orbit Phase 6')
      return
    }
    const actionId = resolveOrbitActionId(sliceId, 'meetings')
    if (actionId) {
      void invokeAction(actionId, { id, title }, {
        workspaceId: activeWorkspaceId ?? '',
        actor: { uid: user?.uid ?? '' },
        canPerform,
        showToast,
        invokedFrom: 'orbit',
        meetingOps: meetingOpsRef.current,
      })
      return
    }
    const lcmDispatch = lcmDispatchers[sliceId]
    if (lcmDispatch) {
      lcmDispatch(id, title)
      return
    }
    console.info('[orbit] unwired', sliceId)
    showToast(`${label}${title ? ` · ${title}` : ''}`)
    // TODO(orbit-handler-missing): sliceIds that hit this fallback still need real primitives — grep console for "[orbit] unwired" to enumerate.
  }, [activeWorkspaceId, canPerform, lcmDispatchers, showToast, user?.uid])
  const orbitDescriptor = useMemo(() => ({
    page: 'meetings',
    centerLabel: 'Meetings',
    onCreate: () => meetingOpsRef.current?.createMeeting?.(),
    contextSlices: () => [],
    targets: withLcmChip({
      'meeting-card': (node: HTMLElement) => buildMeetingCardSlices(node, onOrbitAction),
      'attendee-chip': (node: HTMLElement) => buildAttendeeChipSlices(node, onOrbitAction),
      'agenda-item': (node: HTMLElement) => buildAgendaItemSlices(node, onOrbitAction),
      'action-item': (node: HTMLElement) => buildActionItemSlices(node, onOrbitAction),
      canvas: () => buildMeetingsCanvasSlices(onOrbitAction),
    }, onOrbitAction),
  }), [onOrbitAction])
  useOrbit(orbitDescriptor)
  const { locale, t } = useNls()
  const workspace = workspaces.find((entry) => entry.id === activeWorkspaceId)
  const meetingGateway = useIntegrationGateway('GOOGLE_CALENDAR', { actionKey: 'MEETING_PROVIDER_ACTION' })
  const [meetings, setMeetings] = useState<MeetingRecord[]>([])
  const [huddleSessions, setHuddleSessions] = useState<HuddleSession[]>([])
  const [members, setMembers] = useState<MeetingMember[]>([])
  const [busySlots, setBusySlots] = useState<MeetingBusySlot[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(initialMeetingId)
  const [joiningId, setJoiningId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('WEEK')
  const [scheduleView, setScheduleView] = useState<ScheduleView>('MONTH')
  const [showHuddles, setShowHuddles] = useState(readHuddleVisibility)
  const [sectionMode, setSectionMode] = useState<SectionMode>('SCHEDULE')
  const [focusedDate, setFocusedDate] = useState(() => new Date())
  const [memberSearch, setMemberSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('ALL')
  const prefillGoalId = useLinkedGoalPrefill()
  const [draft, setDraft] = useState<MeetingDraft>(() => draftFromMeeting(null, 'GOOGLE_MEET', prefillGoalId))
  const [createOpen, setCreateOpen] = useState(false)
  const [draftSelection, setDraftSelection] = useState<DraftSelection>(null)
  const [weekDraftSelection, setWeekDraftSelection] = useState<WeekDraftSelection>(null)
  const [draggingMemberUid, setDraggingMemberUid] = useState<string | null>(null)
  const [dragHoverMeetingId, setDragHoverMeetingId] = useState<string | null>(null)
  const [saving, setSaving] = useState<NotifyMode | 'CREATE' | null>(null)
  const [calendarSyncing, setCalendarSyncing] = useState(false)
  const [pendingAction, setPendingAction] = useState<null | (() => void)>(null)
  const [chatMenuOpen, setChatMenuOpen] = useState(false)
  const [docsMenuOpen, setDocsMenuOpen] = useState(false)
  const [docsPickerOpen, setDocsPickerOpen] = useState(false)
  const [assetsPickerOpen, setAssetsPickerOpen] = useState(false)
  const [attachSaving, setAttachSaving] = useState(false)
  const [workspaceFiles, setWorkspaceFiles] = useState<WorkspaceFileMetadata[]>([])
  const [rosterSheetOpen, setRosterSheetOpen] = useState(false)
  const [detailsSheetOpen, setDetailsSheetOpen] = useState(false)
  const lastHydratedSelectedId = useRef<string | null>(null)
  const chatMenuRef = useRef<HTMLDivElement | null>(null)
  const chatCaretRef = useRef<HTMLButtonElement | null>(null)
  const docsCaretRef = useRef<HTMLButtonElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const selectionRef = useRef<{ pointerId: number; startX: number; currentX: number; rect: DOMRect } | null>(null)
  const weekSelectionRef = useRef<{ pointerId: number; startMin: number; currentMin: number; dayIdx: number } | null>(null)
  const weekBodyRef = useRef<HTMLDivElement | null>(null)
  const selected = meetings.find((meeting) => meeting.id === selectedId) ?? null
  const formsPage = useFormDefinitions({
    workspaceId: activeWorkspaceId ?? '',
    lifecycleStatus: 'ACTIVE',
    search: '',
    sort: 'NEWEST',
    cursor: null,
  })
  const designsPage = useDesignAssets(activeWorkspaceId ?? null, { status: 'COMPLETED' })
  const canScheduleMeetings = allowScheduling ?? canPerform('meetings.schedule')
  const canManageMeetings = allowManaging ?? canPerform('meetings.manage')
  const canJoinMeetings = allowJoining ?? canPerform('meetings.join')
  const canCreateMeetingChannel = appMode === 'TEAM' && canPerform('chat.create_private_channel')
  const canCreateCustomMeetingChannel = roleCanPerform(activeRole, 'ADMIN') && canCreateMeetingChannel
  const selectedReadiness = useMeetingReadiness(selected)
  const dirty = useMemo(() => createOpen || !sameDraft(draft, selected), [createOpen, draft, selected])
  const addedParticipantIds = useMemo(() => draft.participantIds.filter((uid) => !participantIds(selected).includes(uid)), [draft.participantIds, selected])
  const removedParticipantIds = useMemo(() => participantIds(selected).filter((uid) => !draft.participantIds.includes(uid)), [draft.participantIds, selected])
  /**
   * effectiveParticipantIds — the list of uids to render on a meeting's
   * calendar bar / pill / block. Rules:
   *   - If the meeting is the selected one AND the draft differs from
   *     saved state, prefer draft.participantIds so drag-added members
   *     appear immediately on the calendar even before Save.
   *   - If a member is currently being dragged AND this meeting is the
   *     drop target being hovered, append the phantom uid so the ghost
   *     avatar previews the drop.
   */
  const participantContext = useMemo(() => ({
    selectedMeetingId: selected?.id ?? null,
    draftParticipantIds: draft.participantIds,
    draggingMemberUid,
    dragHoverMeetingId,
  }), [dragHoverMeetingId, draft.participantIds, draggingMemberUid, selected?.id])
  const getEffectiveParticipantIds = useCallback(
    (meeting: MeetingRecord | null): string[] => getEffectiveParticipantIdsPure(meeting, participantContext),
    [participantContext],
  )
  const getPendingParticipantIds = useCallback(
    (meeting: MeetingRecord | null): Set<string> => getPendingParticipantIdsPure(meeting, participantContext),
    [participantContext],
  )
  const draftDropContext = useMemo(() => ({
    createOpen,
    selectedMeetingId: selected?.id ?? null,
    draftMeetingId: draft.id,
  }), [createOpen, draft.id, selected?.id])
  const effectiveDraftParticipantIds = useMemo(
    () => getEffectiveDraftParticipantIds(participantContext),
    [participantContext],
  )
  const shiftFocus = useCallback((direction: -1 | 1) => {
    setFocusedDate((current) => {
      if (viewMode === 'DAY') return addDays(current, direction)
      if (viewMode === 'WEEK') return addDays(current, direction * 7)
      if (viewMode === 'MONTH') return addMonths(current, direction)
      return new Date(current.getFullYear() + direction, current.getMonth(), current.getDate())
    })
  }, [viewMode])
  const { start: rangeStart, end: rangeEnd } = useMemo(() => getRange(viewMode, focusedDate), [focusedDate, viewMode])
  const labels = useMemo(() => rangeLabels(viewMode, rangeStart, rangeEnd, locale), [locale, rangeEnd, rangeStart, viewMode])
  const bars = useMemo(() => layoutMeetings(meetings, rangeStart, rangeEnd), [meetings, rangeEnd, rangeStart])
  const draftBar = useMemo(() => {
    if (!createOpen || draft.id) return null
    const begins = inputToDate(draft.startInput).getTime()
    const rangeStartMs = rangeStart.getTime()
    const rangeEndMs = rangeEnd.getTime()
    const finishes = begins + clampDuration(draft.durationMinutes) * 60_000
    if (finishes <= rangeStartMs || begins >= rangeEndMs) return null
    const span = rangeEndMs - rangeStartMs
    const clampedStart = Math.max(begins, rangeStartMs)
    const clampedEnd = Math.min(finishes, rangeEndMs)
    return {
      left: ((clampedStart - rangeStartMs) / span) * 100,
      width: Math.max(5, ((clampedEnd - clampedStart) / span) * 100),
      top: (Math.max(-1, ...bars.map((bar) => bar.lane)) + 1) * 74 + 28,
    }
  }, [bars, createOpen, draft.durationMinutes, draft.id, draft.startInput, rangeEnd, rangeStart])

  useEffect(() => {
    if (!activeWorkspaceId || !workspace || !user) return
    const demo = getDemoWorkspaceData(activeWorkspaceId)
    if (demo) {
      setMeetings(demo.meetings)
      setHuddleSessions([])
      setSelectedId((current) => current && demo.meetings.some((meeting) => meeting.id === current) ? current : pickLiveMeetingId(demo.meetings))
      setMembers(demo.members.map((member) => ({
        id: member.uid,
        name: member.displayName,
        email: member.email,
        role: member.role,
      } as unknown as MeetingMember)))
      setBusySlots([])
      return
    }
    const uid = user.uid
    const stopMeetings = subscribeMeetings(activeWorkspaceId, (next) => {
      setMeetings(next)
      setSelectedId((current) => current && next.some((meeting) => meeting.id === current) ? current : pickLiveMeetingId(next))
    }, (error) => showToast(error.message, 'error'), { restricted: !!isAccessRestricted, uid })
    const stopHuddleSessions = subscribeHuddleSessions(activeWorkspaceId, setHuddleSessions, (error) => showToast(error.message, 'error'), { restricted: !!isAccessRestricted, uid })
    const stopMembers = subscribeMeetingMembers(activeWorkspaceId, { id: workspace.ownerId, name: 'Workspace owner' }, appMode === 'PERSONAL', setMembers, (error) => showToast(error.message, 'error'))
    const stopBusy = appMode === 'TEAM' ? subscribeGoogleCalendarBusySlots(activeWorkspaceId, setBusySlots) : () => undefined
    return () => { stopMeetings(); stopHuddleSessions(); stopMembers(); stopBusy() }
  }, [activeWorkspaceId, appMode, isAccessRestricted, showToast, user, workspace])

  useEffect(() => {
    try {
      window.localStorage.setItem(HUDDLE_VISIBILITY_STORAGE_KEY, String(showHuddles))
    } catch {
      // Storage can be unavailable in private or embedded browser contexts.
    }
  }, [showHuddles])

  useEffect(() => {
    if (!activeWorkspaceId || !user?.uid) return
    return subscribeWorkspaceFiles(activeWorkspaceId, user.uid, false, setWorkspaceFiles)
  }, [activeWorkspaceId, user?.uid])

  useEffect(() => {
    if (initialMeetingId) setSelectedId(initialMeetingId)
  }, [initialMeetingId])

  useEffect(() => {
    if (lastHydratedSelectedId.current !== selectedId) {
      lastHydratedSelectedId.current = selectedId
      setDraft(draftFromMeeting(selected))
      setCreateOpen(false)
      return
    }
    if (!dirty) setDraft(draftFromMeeting(selected))
    // The selected meeting is the source of truth when the current draft is clean.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selected?.updatedAt])

  const requestOrRun = useCallback((action: () => void) => {
    if (!dirty) { action(); return }
    setPendingAction(() => action)
  }, [dirty])

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ onApproved: () => void; handled?: boolean }>).detail
      if (!detail?.onApproved) return
      detail.handled = true
      requestOrRun(detail.onApproved)
    }
    window.addEventListener('timelining:meetings-request-close', handler)
    return () => window.removeEventListener('timelining:meetings-request-close', handler)
  }, [requestOrRun])

  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (!dirty) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  // Chat and docs menus are portalled through FloatingMenu, which owns its own
  // outside-click and Escape handlers.

  const readiness = useMemo(() => Object.fromEntries(meetings.map((meeting) => {
    const agenda = meeting.agendaNodes ?? []
    const results = agenda.map((node) => getAgendaNodeReadiness(node.nodeType, node.nodeId, { elements, checklistItems }))
    const ready = results.filter((result) => result.isReady).length
    return [meeting.id, agenda.length ? Math.round(ready / agenda.length * 100) : 0]
  })), [checklistItems, elements, meetings])

  const visibleMembers = useMemo(() => members
    .filter((member) => roleFilter === 'ALL' || member.role === roleFilter)
    .filter((member) => `${member.name} ${member.role}`.toLowerCase().includes(memberSearch.toLowerCase()))
    .sort((a, b) => a.role.localeCompare(b.role) || a.name.localeCompare(b.name)), [memberSearch, members, roleFilter])

  const memberById = useMemo(() => new Map(members.map((member) => [member.uid, member])), [members])
  const channelMemberIds = useCallback((channelId: string) => {
    const channel = channelsList.find((entry) => entry.id === channelId)
    if (!channel) return []
    if (channel.type === 'public') return members.map((member) => member.uid)
    return Object.keys(channel.roles)
  }, [channelsList, members])
  const linkedChannels = useMemo(() => draft.chatChannelIds.flatMap((channelId) => {
    const channel = channelsList.find((entry) => entry.id === channelId)
    return channel ? [channel] : []
  }), [channelsList, draft.chatChannelIds])
  const meetingChannelMemberIds = useMemo(() => {
    const ids = new Set(participantIds(selected))
    if (user?.uid) ids.add(user.uid)
    return Array.from(ids).sort()
  }, [selected, user?.uid])
  const existingMeetingChannel = useMemo(() => {
    if (!meetingChannelMemberIds.length) return null
    const key = meetingChannelMemberIds.join('|')
    return channelsList.find((channel) => channel.type === 'private' && Object.keys(channel.roles).sort().join('|') === key) ?? null
  }, [channelsList, meetingChannelMemberIds])
  const meetingChannel = linkedChannels[0] ?? existingMeetingChannel
  const selectedStart = inputToDate(draft.startInput)
  const currentHuddlePresence = useMemo(
    () => huddlePresence.find((presence) => presence.uid === user?.uid) ?? null,
    [huddlePresence, user?.uid],
  )
  const isListening = currentHuddlePresence?.isListening ?? false
  const isSpeaking = currentHuddlePresence?.isSpeaking ?? false
  const listeningCount = huddlePresence.filter((presence) => presence.isListening).length
  const speakingCount = huddlePresence.filter((presence) => presence.isSpeaking).length
  const canEditDraft = createOpen ? canScheduleMeetings : canManageMeetings

  const selectMeeting = (meetingId: string) => requestOrRun(() => {
    setSelectedId(meetingId)
    if (isMobile) setDetailsSheetOpen(true)
  })
  const addParticipant = (uid: string, meetingId: string | null = selectedId) => {
    if (!canEditDraft) return
    if (!meetingId) { showToast('Drop onto a meeting to add this person.', 'info'); return }
    if (shouldApplyParticipantDropToCurrentDraft(meetingId, draftDropContext)) {
      setDraft((current) => {
        if (current.participantIds.includes(uid)) {
          showToast('This person is already added to the meeting.', 'info')
          return current
        }
        showToast('Member added. Save to send updates.', 'success')
        return { ...current, participantIds: [...current.participantIds, uid] }
      })
      if (isMobile) setDetailsSheetOpen(true)
      return
    }
    requestOrRun(() => {
      const target = meetings.find((meeting) => meeting.id === meetingId)
      if (!target) { showToast('No meeting there.', 'info'); return }
      setSelectedId(meetingId)
      setDraft((current) => {
        const base = current.id === meetingId ? current : draftFromMeeting(target)
        if (base.participantIds.includes(uid)) {
          showToast('This person is already added to the meeting.', 'info')
          return base
        }
        return { ...base, participantIds: [...base.participantIds, uid] }
      })
      showToast('Member added. Save to send updates.', 'success')
    })
  }

  const addChannelToDraft = (channelId: string) => {
    if (!canEditDraft) return
    requestOrRun(() => {
      const channel = channelsList.find((entry) => entry.id === channelId)
      if (!channel) return
      const channelIds = channelMemberIds(channelId)
      setDraft((current) => ({
        ...current,
        sourceProvider: current.sourceProvider,
        chatChannelIds: current.chatChannelIds.includes(channelId)
          ? current.chatChannelIds
          : [...current.chatChannelIds, channelId],
        participantIds: Array.from(new Set([...current.participantIds, ...channelIds])),
      }))
      showToast(`#${channel.slug || channel.name} linked. Channel members were added.`, 'success')
    })
  }

  const removeChannelFromDraft = (channelId: string) => {
    if (!canEditDraft) return
    setDraft((current) => ({
      ...current,
      chatChannelIds: current.chatChannelIds.filter((id) => id !== channelId),
    }))
  }

  const attachments: MeetingAttachment[] = selected?.attachments ?? []
  const workspaceItemAttachments = useMemo(
    () => attachments.filter((entry): entry is Extract<MeetingAttachment, { kind: 'ENTITY' }> => entry.kind === 'ENTITY'),
    [attachments],
  )
  const uploadAttachments = useMemo(
    () => attachments.filter((entry): entry is Extract<MeetingAttachment, { kind: 'UPLOAD' }> => entry.kind === 'UPLOAD'),
    [attachments],
  )

  const commitAttachments = useCallback(async (next: MeetingAttachment[]) => {
    if (!selected || !canManageMeetings) return false
    try {
      await updateMeetingAttachments(selected.id, next)
      return true
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not update attachments.', 'error')
      return false
    }
  }, [canManageMeetings, selected, showToast])

  const handleAttachUpload = useCallback(async (file: File) => {
    if (!selected || !activeWorkspaceId || !user) return
    setAttachSaving(true)
    try {
      await executeAttachUpload({
        file,
        workspaceId: activeWorkspaceId,
        meetingId: selected.id,
        userId: user.uid,
        attachments,
        uploadArtifact: uploadWorkspaceArtifact,
        commitAttachments,
        showToast,
      })
    } finally {
      setAttachSaving(false)
    }
  }, [activeWorkspaceId, attachments, commitAttachments, selected, showToast, user])

  const handleAttachEntity = useCallback(async (reference: WorkspaceItemReference) => {
    if (!selected || !user) return
    setAttachSaving(true)
    try {
      const ok = await executeAttachEntity({
        reference,
        userId: user.uid,
        attachments,
        commitAttachments,
        showToast,
      })
      if (ok) {
        setDocsPickerOpen(false)
      }
    } finally {
      setAttachSaving(false)
    }
  }, [attachments, commitAttachments, selected, showToast, user])

  const handleAttachEntities = useCallback(async (references: WorkspaceItemReference[]) => {
    if (!selected || !user) return
    const attachable = references.filter((r): r is WorkspaceItemReference & { kind: MeetingAttachmentEntity['refKind'] } =>
      isAttachableMeetingRefKind(r.kind),
    )
    if (!attachable.length) return
    const existingIds = new Set(workspaceItemAttachments.map((entry) => entry.refId))
    const uniqueReferences = attachable.filter((reference) => !existingIds.has(reference.id) && (existingIds.add(reference.id), true))
    if (!uniqueReferences.length) {
      showToast('Those items are already attached.', 'info')
      return
    }
    const next: MeetingAttachment[] = [
      ...attachments,
      ...uniqueReferences.map((reference) => ({
        kind: 'ENTITY' as const,
        refId: reference.id,
        refKind: reference.kind,
        refLabel: reference.label,
        parentId: reference.parentId,
        addedAt: new Date().toISOString(),
        addedBy: user.uid,
      })),
    ]
    setAttachSaving(true)
    try {
      const committed = await commitAttachments(next)
      if (!committed) return
      setAssetsPickerOpen(false)
      showToast(`${uniqueReferences.length} item${uniqueReferences.length === 1 ? '' : 's'} attached to the meeting.`, 'success')
    } finally {
      setAttachSaving(false)
    }
  }, [attachments, commitAttachments, selected, showToast, user, workspaceItemAttachments])

  const handleDetachAttachment = useCallback(async (index: number) => {
    if (!selected) return
    const next = attachments.filter((_, cursor) => cursor !== index)
    await commitAttachments(next)
  }, [attachments, commitAttachments, selected])

  const openCreateAt = (clientX: number, rect: DOMRect, durationMinutes = 30, explicitStart?: Date) => {
    if (!canScheduleMeetings) return
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const start = explicitStart ?? new Date(rangeStart.getTime() + ratio * (rangeEnd.getTime() - rangeStart.getTime()))
    if (!explicitStart && viewMode !== 'DAY') start.setHours(9, 0, 0, 0)
    else if (!explicitStart) start.setMinutes(Math.round(start.getMinutes() / 30) * 30, 0, 0)
    requestOrRun(() => {
      setDraft({ ...draftFromMeeting(null, 'GOOGLE_MEET', prefillGoalId), title: 'New meeting', startInput: toLocalInput(start), durationMinutes: clampDuration(durationMinutes) })
      setCreateOpen(true)
      setSelectedId(null)
      if (isMobile) setDetailsSheetOpen(true)
    })
  }

  const openScheduleModal = () => {
    if (!canScheduleMeetings) return
    requestOrRun(() => {
      const start = new Date()
      start.setMinutes(Math.ceil(start.getMinutes() / 30) * 30, 0, 0)
      setDraft({ ...draftFromMeeting(null, 'GOOGLE_MEET', prefillGoalId), title: 'New meeting', startInput: toLocalInput(start), durationMinutes: 30 })
      setCreateOpen(true)
      setSelectedId(null)
      setSectionMode('SCHEDULE')
      if (isMobile) setDetailsSheetOpen(true)
    })
  }

  meetingOpsRef.current = createMeetingOps({
    openScheduleModal,
    setSelectedId,
    deleteMeeting,
    showToast,
    hasEditableDraft: canEditDraft && (createOpen || Boolean(selectedId)),
    getDraft: () => draft,
    setDraft,
  })

  const handleBoardPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (!canScheduleMeetings || event.button !== 0) return
    if ((event.target as HTMLElement).closest('.meetingTimelineBar, .meetingTimelineTick')) return
    const rect = event.currentTarget.getBoundingClientRect()
    selectionRef.current = { pointerId: event.pointerId, startX: event.clientX, currentX: event.clientX, rect }
    event.currentTarget.setPointerCapture?.(event.pointerId)
    setDraftSelection({ left: Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100)), width: 0.75 })
  }

  const handleBoardPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const selection = selectionRef.current
    if (!selection || selection.pointerId !== event.pointerId) return
    selection.currentX = event.clientX
    const start = Math.max(0, Math.min(1, (selection.startX - selection.rect.left) / selection.rect.width))
    const current = Math.max(0, Math.min(1, (event.clientX - selection.rect.left) / selection.rect.width))
    const left = Math.min(start, current) * 100
    const width = Math.max(0.75, Math.abs(current - start) * 100)
    setDraftSelection({ left, width })
  }

  const handleBoardPointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    const selection = selectionRef.current
    if (!selection || selection.pointerId !== event.pointerId) return
    selectionRef.current = null
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    setDraftSelection(null)
    const startRatio = Math.max(0, Math.min(1, (selection.startX - selection.rect.left) / selection.rect.width))
    const endRatio = Math.max(0, Math.min(1, (selection.currentX - selection.rect.left) / selection.rect.width))
    const deltaRatio = Math.abs(endRatio - startRatio)
    if (deltaRatio < 0.006) {
      openCreateAt(selection.startX, selection.rect)
      return
    }
    const rangeMs = rangeEnd.getTime() - rangeStart.getTime()
    const start = snapDateToMinutes(new Date(rangeStart.getTime() + Math.min(startRatio, endRatio) * rangeMs))
    const end = snapDateToMinutes(new Date(rangeStart.getTime() + Math.max(startRatio, endRatio) * rangeMs))
    const duration = Math.max(MIN_DURATION, Math.round((end.getTime() - start.getTime()) / 60_000))
    openCreateAt(selection.startX, selection.rect, duration, start)
  }

  const handleBoardDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (!canEditDraft) return
    const uid = event.dataTransfer.getData('text/member-uid')
    setDragHoverMeetingId(null)
    setDraggingMemberUid(null)
    if (uid) showToast('No meeting there.', 'info')
  }

  const join = async (meeting: MeetingRecord) => {
    if (!canJoinMeetings) {
      showToast('You do not have permission to join meetings in this workspace.', 'error')
      return
    }
    setJoiningId(meeting.id)
    try {
      const { joinUrl } = await createMeetingProviderAdapter(meeting.sourceProvider).joinMeeting(meeting.id)
      if (meeting.sourceProvider === 'HUDDLE') {
        const channelId = meeting.chatChannelId ?? meeting.chatChannelIds?.[0] ?? activeChannelId ?? channelsList[0]?.id ?? null
        if (!channelId) throw new Error('Link a chat channel before joining this huddle.')
        setActiveChannelId(channelId)
        await setHuddleState({ isListening: true, isSpeaking: false }, channelId)
        showToast('Joined huddle.', 'success')
        onOpenChat?.(meeting)
        return
      }
      window.open(joinUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Join gateway failed.', 'error')
    } finally {
      setJoiningId(null)
    }
  }

  const openLinkedChat = (meeting: MeetingRecord) => {
    const channelId = meeting.chatChannelId ?? meeting.chatChannelIds?.[0] ?? null
    if (channelId) setActiveChannelId(channelId)
    onOpenChat?.(meeting)
  }

  const linkChannelToSelectedMeeting = async (channelId: string, meeting: MeetingRecord) => {
    if (!canManageMeetings) return
    const nextChannelIds = Array.from(new Set([...(meeting.chatChannelIds ?? (meeting.chatChannelId ? [meeting.chatChannelId] : [])), channelId]))
    await updateMeetingDetails({
      meetingId: meeting.id,
      title: meeting.title,
      description: meeting.description,
      scheduledAt: meetingDate(meeting.scheduledAt).toISOString(),
      durationMinutes: meeting.durationMinutes ?? 30,
      participantIds: participantIds(meeting),
      notifyMode: 'ALL',
      chatChannelIds: nextChannelIds,
    })
  }

  const openMeetingChannel = async (meeting: MeetingRecord) => {
    if (!onOpenChat) return
    setChatMenuOpen(false)
    if (meetingChannel) {
      setActiveChannelId(meetingChannel.id)
      onOpenChat(meeting)
      return
    }
    if (!canCreateMeetingChannel) {
      showToast('Your workspace role cannot create private meeting channels.', 'error')
      return
    }
    if (meetingChannelMemberIds.length < 2) {
      showToast('Add at least two meeting members before creating a meeting channel.', 'info')
      return
    }
    try {
      const channelId = await createChannel({
        name: `Meeting ${meeting.title}`.slice(0, 64),
        type: 'private',
        initialMemberIds: meetingChannelMemberIds,
      })
      setActiveChannelId(channelId)
      void linkChannelToSelectedMeeting(channelId, meeting).catch((error) => {
        showToast(error instanceof Error ? error.message : 'Channel created, but meeting link could not be saved.', 'error')
      })
      onOpenChat(meeting)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Meeting channel could not be created.', 'error')
    }
  }

  const openCustomMeetingChannelDraft = (meeting: MeetingRecord) => {
    if (!onOpenChat || !canCreateCustomMeetingChannel) return
    setChatMenuOpen(false)
    requestCreateChannelDraft({
      name: `Meeting ${meeting.title}`.slice(0, 64),
      type: 'private',
      initialMemberIds: meetingChannelMemberIds,
    })
    onOpenChat(meeting)
  }

  const toggleListening = () => {
    void setHuddleState({ isListening: !isListening, isSpeaking: isListening ? false : isSpeaking }).catch((error) => {
      showToast(error instanceof Error ? error.message : 'Huddle could not be updated.', 'error')
    })
  }

  const toggleSpeaking = () => {
    const nextSpeaking = !isSpeaking
    void setHuddleState({ isListening: nextSpeaking ? true : isListening, isSpeaking: nextSpeaking }).catch((error) => {
      showToast(error instanceof Error ? error.message : 'Huddle could not be updated.', 'error')
    })
  }

  const saveCreate = async () => {
    if (!canScheduleMeetings) return
    if (!activeWorkspaceId || !workspace || !draft.title.trim()) return
    setSaving('CREATE')
    try {
      const input: MeetingCreateInput = {
        workspaceId: activeWorkspaceId,
        title: draft.title,
        description: draft.description,
        sourceProvider: draft.sourceProvider,
        scheduledAt: inputToDate(draft.startInput).toISOString(),
        durationMinutes: clampDuration(draft.durationMinutes),
        timezone: workspace.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
        agendaMarkdown: draft.agendaMarkdown,
        agendaNodes: draft.agendaNodes,
        participantIds: draft.participantIds,
        externalInvitees: [],
        chatChannelIds: draft.chatChannelIds,
        scopeNodeId: draft.scopeNodeId ?? null,
      }
      const result = await createMeetingProviderAdapter(draft.sourceProvider).createMeeting(input)
      setSelectedId(result.meetingId)
      setCreateOpen(false)
      showToast('Meeting saved and updates sent.', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Meeting could not be saved.', 'error')
    } finally {
      setSaving(null)
    }
  }

  const saveExisting = async (notifyMode: NotifyMode) => {
    if (!canManageMeetings) return
    if (!selected || !draft.title.trim()) return
    setSaving(notifyMode)
    try {
      await updateMeetingDetails({
        meetingId: selected.id,
        title: draft.title,
        description: draft.description,
        scheduledAt: inputToDate(draft.startInput).toISOString(),
        durationMinutes: clampDuration(draft.durationMinutes),
        participantIds: draft.participantIds,
        notifyMode,
        chatChannelIds: draft.chatChannelIds,
        ...meetingScopeNodeIdPatch(draft.scopeNodeId, selected.scopeNodeId),
      })
      const agendaChanged = draft.agendaMarkdown !== (selected.agendaMarkdown ?? '')
        || JSON.stringify(draft.agendaNodes) !== JSON.stringify(selected.agendaNodes ?? [])
      if (agendaChanged) {
        await updateMeetingAgenda(selected.id, draft.agendaMarkdown, draft.agendaNodes)
      }
      showToast(
        notifyMode === 'ALL' ? 'Meeting updated for all participants.'
          : notifyMode === 'CHANGED' ? 'Meeting updated for changed participants.'
          : 'Meeting updated silently — no notifications sent.',
        'success',
      )
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Meeting could not be updated.', 'error')
    } finally {
      setSaving(null)
    }
  }

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const cancelSelectedMeeting = async () => {
    if (!selected || !canManageMeetings) return
    setSaving('ALL')
    try {
      await cancelMeeting(selected.id)
      showToast('Meeting cancelled.', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Meeting could not be cancelled.', 'error')
    } finally {
      setSaving(null)
    }
  }

  const pushSelectedMeetingToCalendar = async () => {
    if (!selected || !canManageMeetings) return
    setCalendarSyncing(true)
    try {
      const result = await resyncMeetingCalendar(selected.id)
      showToast(result.status === 'SYNCED' ? 'Meeting pushed to Google Calendar.' : result.status === 'SKIPPED_NO_CONNECTION' ? 'Connect Google Calendar in Link Hub first.' : 'Google Calendar needs attention.', result.status === 'SYNCED' ? 'success' : 'error')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not push this meeting to Google Calendar.', 'error')
    } finally {
      setCalendarSyncing(false)
    }
  }

  const pushVisibleWeekToCalendar = async () => {
    if (!activeWorkspaceId || !canManageMeetings) return
    const calendarWeekStart = scheduleView === 'WEEK' ? weekStart : startOfDay(focusedDate)
    const weekEnd = addDays(calendarWeekStart, 7)
    setCalendarSyncing(true)
    try {
      const result = await bulkPushMeetingsToGoogleCalendar({ workspaceId: activeWorkspaceId, startAt: calendarWeekStart.toISOString(), endAt: weekEnd.toISOString() })
      showToast(`${result.synced} meeting${result.synced === 1 ? '' : 's'} pushed to Google Calendar${result.failed ? `; ${result.failed} need attention` : ''}.`, result.failed ? 'error' : 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not push this week to Google Calendar.', 'error')
    } finally {
      setCalendarSyncing(false)
    }
  }

  const deleteSelectedMeeting = async () => {
    if (!selected || !canManageMeetings) return
    setDeleting(true)
    try {
      await deleteMeeting(selected.id)
      showToast('Meeting deleted.', 'success')
      setDeleteConfirmOpen(false)
      setSelectedId(null)
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Meeting could not be deleted.', 'error')
    } finally {
      setDeleting(false)
    }
  }

  const resizeBar = (event: ReactPointerEvent<HTMLSpanElement>, meeting: MeetingRecord) => {
    if (!canManageMeetings) return
    event.stopPropagation()
    event.preventDefault()
    const start = meetingDate(meeting.scheduledAt).getTime()
    const originX = event.clientX
    const originDuration = meeting.durationMinutes ?? 30
    const board = event.currentTarget.closest('.meetingTimelineBoard') as HTMLElement | null
    if (!board) return
    const rect = board.getBoundingClientRect()
    const minutesPerPx = ((rangeEnd.getTime() - rangeStart.getTime()) / 60_000) / rect.width
    const move = (moveEvent: PointerEvent) => {
      const delta = moveEvent.clientX - originX
      const next = clampDuration(Math.round((originDuration + delta * minutesPerPx) / 15) * 15)
      setSelectedId(meeting.id)
      setDraft((current) => ({ ...(current.id === meeting.id ? current : draftFromMeeting(meeting)), durationMinutes: next, startInput: toLocalInput(new Date(start)) }))
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up, { once: true })
  }

  // Hooks stay above the workspace/user guard below so the hook order never changes between renders.
  const upcomingCount = meetings.filter((meeting) => meetingDate(meeting.scheduledAt).getTime() >= Date.now()).length

  const monthCursor = useMemo(() => new Date(focusedDate.getFullYear(), focusedDate.getMonth(), 1), [focusedDate])
  const monthLabel = monthCursor.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
  const monthGridDays = useMemo(() => {
    const firstOfMonth = monthCursor
    const mondayOffset = (firstOfMonth.getDay() + 6) % 7
    const gridStart = addDays(firstOfMonth, -mondayOffset)
    return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index))
  }, [monthCursor])
  const scheduleEntries = useMemo(
    () => buildMeetingScheduleEntries(meetings, huddleSessions, showHuddles),
    [huddleSessions, meetings, showHuddles],
  )
  const meetingsByDayKey = useMemo(() => bucketScheduleEntriesByDay(scheduleEntries), [scheduleEntries])
  const meetingColorMap = useMemo(() => {
    const map = new Map<string, string>()
    meetings.forEach((meeting, index) => map.set(meeting.id, COLORS[index % COLORS.length]))
    return map
  }, [meetings])
  const todayKey = useMemo(() => {
    const now = new Date()
    return `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`
  }, [])

  const weekStart = useMemo(() => {
    const day = startOfDay(focusedDate)
    const mondayOffset = (day.getDay() + 6) % 7
    return addDays(day, -mondayOffset)
  }, [focusedDate])
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)), [weekStart])
  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart])
  const weekLabel = useMemo(() => {
    const endInclusive = addDays(weekStart, 6)
    const sameMonth = weekStart.getMonth() === endInclusive.getMonth()
    const sameYear = weekStart.getFullYear() === endInclusive.getFullYear()
    if (sameMonth) return `${weekStart.toLocaleDateString(locale, { month: 'long', day: 'numeric' })} – ${endInclusive.getDate()}, ${endInclusive.getFullYear()}`
    const startPart = weekStart.toLocaleDateString(locale, { month: 'short', day: 'numeric' })
    const endPart = endInclusive.toLocaleDateString(locale, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' })
    return `${startPart} – ${endPart}${sameYear ? `, ${endInclusive.getFullYear()}` : ''}`
  }, [locale, weekStart])
  const isCurrentWeekOnScreen = useMemo(() => {
    const now = Date.now()
    return now >= weekStart.getTime() && now < weekEnd.getTime()
  }, [weekEnd, weekStart])
  const weekMeetingsByDay = useMemo(() => {
    return layoutScheduleEntriesForWeek(scheduleEntries, weekStart, weekEnd)
  }, [scheduleEntries, weekEnd, weekStart])
  const weekDraftBlock = useMemo(() => {
    if (!createOpen || draft.id) return null
    const begins = inputToDate(draft.startInput).getTime()
    const rangeStartMs = weekStart.getTime()
    const rangeEndMs = weekEnd.getTime()
    const finishes = begins + clampDuration(draft.durationMinutes) * 60_000
    if (finishes <= rangeStartMs || begins >= rangeEndMs) return null
    const dayIdx = Math.floor((begins - rangeStartMs) / DAY_MS)
    if (dayIdx < 0 || dayIdx >= 7) return null
    const dayStart = rangeStartMs + dayIdx * DAY_MS
    const startMin = Math.max(0, (Math.max(begins, dayStart) - dayStart) / 60_000)
    const endMin = Math.min(WEEK_TOTAL_MINUTES, (Math.min(finishes, dayStart + DAY_MS) - dayStart) / 60_000)
    return { dayIdx, startMin, endMin }
  }, [createOpen, draft.durationMinutes, draft.id, draft.startInput, weekEnd, weekStart])
  const nowMarker = useMemo(() => {
    const now = new Date()
    const rangeStartMs = weekStart.getTime()
    const rangeEndMs = weekEnd.getTime()
    if (now.getTime() < rangeStartMs || now.getTime() >= rangeEndMs) return null
    const dayIdx = Math.floor((now.getTime() - rangeStartMs) / DAY_MS)
    return { dayIdx, minutes: now.getHours() * 60 + now.getMinutes() }
  }, [weekEnd, weekStart])

  useEffect(() => {
    if (scheduleView !== 'WEEK') return
    const node = weekBodyRef.current
    if (!node) return
    const now = new Date()
    const inWeek = now.getTime() >= weekStart.getTime() && now.getTime() < weekEnd.getTime()
    const targetMinutes = inWeek ? now.getHours() * 60 + now.getMinutes() : 8 * 60
    const targetPx = (targetMinutes / WEEK_TOTAL_MINUTES) * WEEK_TOTAL_HEIGHT_PX
    node.scrollTop = Math.max(0, targetPx - node.clientHeight / 2)
  }, [scheduleView, weekEnd, weekStart])

  const meetingsRailScroll = useEdgeFadeScroll<HTMLDivElement>()

  if (!workspace || !user) return null

  const rangeTitle = rangeStart.toLocaleDateString(locale, viewMode === 'YEAR' ? { year: 'numeric' } : { month: 'long', day: 'numeric', year: 'numeric' })
  const meetingViewLabels = getMeetingViewLabels(t)
  const meetingSectionLabels = getMeetingSectionLabels(t)
  const meetingDirtyDialog = getMeetingDirtyDialog(t)

  const rosterHeader: ReactNode = (
    <header>
      <h2>{t('NLSMeetingsLabelMembers')}</h2>
      <p>{canEditDraft ? 'Drag a person onto a meeting to add them.' : 'People attached to workspace meetings.'}</p>
    </header>
  )

  const rosterSearchAndFilters: ReactNode = (
    <>
      <TextInput
        type="search"
        leadingIcon={<SearchRoundedIcon />}
        placeholder="Search people"
        aria-label="Search people"
        value={memberSearch}
        onChange={setMemberSearch}
      />
      <div className="meetingRosterFilters" aria-label="Member role filter">
        <Select<string>
          trigger="labeledPill"
          ariaLabel="Filter members by role"
          label={<TuneRoundedIcon fontSize="small" />}
          value={roleFilter}
          onChange={setRoleFilter}
          options={[
            { value: 'ALL', label: 'All' },
            { value: 'ADMIN', label: t(roleLabelKey('ADMIN')) },
            { value: 'EDITOR', label: t(roleLabelKey('EDITOR')) },
            { value: 'VIEWER', label: t(roleLabelKey('VIEWER')) },
            { value: 'HELPER', label: t(roleLabelKey('HELPER')) },
          ]}
        />
      </div>
    </>
  )

  const rosterList: ReactNode = (
    <div className="meetingRosterList">
      {visibleMembers.length === 0 ? (
        <p className="meetingRosterEmpty">No members match the current filters.</p>
      ) : visibleMembers.map((member) => (
        <Pressable
          key={member.uid}
          className={`meetingRosterPerson${draggingMemberUid === member.uid ? ' isDragging' : ''}`}
          draggable={canEditDraft}
          onDragStart={(event) => {
            event.dataTransfer.setData('text/member-uid', member.uid)
            event.dataTransfer.effectAllowed = 'copy'
            setDraggingMemberUid(member.uid)
          }}
          onDragEnd={() => {
            setDraggingMemberUid(null)
            setDragHoverMeetingId(null)
          }}
          onClick={() => {
            const target = rosterClickTargetId({
              createOpen,
              draftMeetingId: draft.id,
              selectedMeetingId: selectedId,
            })
            if (target) addParticipant(member.uid, target)
          }}
        >
          <span>{initials(member.name)}</span>
          <strong>{member.name}</strong>
          <em>{formatRoleLabel(t, member.role)}</em>
        </Pressable>
      ))}
    </div>
  )

  const channelList: ReactNode = (
    <section className="meetingChannelList" aria-label="Chat channels">
      <div className="meetingSidecarHeading">
        <span className="settingsEyebrow">{t('NLSMeetingsLabelChannels')}</span>
        <GhostBadge tone="muted">{channelsList.length}</GhostBadge>
      </div>
      {channelsList.length === 0 ? (
        <p className="meetingRosterEmpty">{t('NLSMeetingsEmptyNoChannels')}</p>
      ) : channelsList.map((channel) => {
        const selectedChannel = draft.chatChannelIds.includes(channel.id)
        const count = channel.type === 'public' ? members.length : Object.keys(channel.roles).length
        return (
          <Pressable
            key={channel.id}
            className={`meetingChannelRow${selectedChannel ? ' isActive' : ''}`}
            aria-pressed={selectedChannel}
            disabled={!canEditDraft}
            onClick={() => addChannelToDraft(channel.id)}
          >
            <span>{channel.type === 'private' ? 'lock' : '#'}</span>
            <strong>{channel.slug || channel.name}</strong>
            <em>{count} member{count === 1 ? '' : 's'}</em>
          </Pressable>
        )
      })}
    </section>
  )

  const rosterContent: ReactNode = (
    <>
      {rosterHeader}
      {rosterSearchAndFilters}
      {rosterList}
      {channelList}
    </>
  )

  const railPeoplePanel: ReactNode = (
    <section className="meetingRailPeople" aria-label="Members and channels">
      <div className="meetingRailSectionHead">
        <span className="meetingRailSectionEyebrow">{t('NLSMeetingsLabelPeople')}</span>
        <GhostBadge tone="muted">{visibleMembers.length}</GhostBadge>
      </div>
      {rosterSearchAndFilters}
      {rosterList}
      {channelList}
    </section>
  )

  const timelineBoard: ReactNode = (
    <section
      className={`meetingTimelineBoard meetingTimelineBoard--${viewMode.toLowerCase()}`}
      onPointerDown={handleBoardPointerDown}
      onPointerMove={handleBoardPointerMove}
      onPointerUp={handleBoardPointerUp}
      onPointerCancel={() => { selectionRef.current = null; setDraftSelection(null) }}
      onDragOver={(event) => event.preventDefault()}
      onDrop={handleBoardDrop}
    >
      <div className="meetingTimelineGrid">
        {labels.map((label) => (
          <div key={label.key} className="meetingTimelineTick" style={{ left: `${label.left}%` }}>
            <span>{label.label}</span>
            {label.sub && <small>{label.sub}</small>}
          </div>
        ))}
      </div>
      {bars.length === 0 && !draftBar ? (
        <div className="meetingTimelineEmpty">
          <EventNoteRoundedIcon />
          <strong>{t('NLSMeetingsEmptyTimelineTitle')}</strong>
          <span>{canScheduleMeetings ? t('NLSMeetingsEmptyTimelineCanSchedule') : t('NLSMeetingsEmptyTimelineReadonly')}</span>
        </div>
      ) : null}
      <div className="meetingTimelineLanes" style={{ minHeight: `${Math.max(360, (Math.max(5, ...bars.map((bar) => bar.lane), draftBar ? Math.round((draftBar.top - 28) / 74) : 0) + 1) * 74)}px` }}>
        {draftSelection ? (
          <span
            className="meetingTimelineSelection"
            style={{ left: `${draftSelection.left}%`, width: `${draftSelection.width}%` }}
            aria-hidden="true"
          />
        ) : null}
        {bars.map((bar) => {
          const pending = getPendingParticipantIds(bar.meeting)
          const effective = getEffectiveParticipantIds(bar.meeting).slice(0, 3)
          return (
          <Pressable
            key={bar.meeting.id}
            className={`meetingTimelineBar${selectedId === bar.meeting.id ? ' isSelected' : ''}${dragHoverMeetingId === bar.meeting.id ? ' isDropTarget' : ''}`}
            style={{ left: `${bar.left}%`, width: `${bar.width}%`, top: `${bar.lane * 74 + 28}px`, '--meeting-bar': bar.color } as CSSProperties}
            onClick={(event) => { event.stopPropagation(); selectMeeting(bar.meeting.id) }}
            onDragOver={(event) => {
              if (!event.dataTransfer.types.includes('text/member-uid')) return
              event.preventDefault()
              event.dataTransfer.dropEffect = 'copy'
              if (dragHoverMeetingId !== bar.meeting.id) setDragHoverMeetingId(bar.meeting.id)
            }}
            onDragLeave={(event) => {
              const related = event.relatedTarget as Node | null
              if (related && event.currentTarget.contains(related)) return
              if (dragHoverMeetingId === bar.meeting.id) setDragHoverMeetingId(null)
            }}
            onDrop={(event) => {
              event.preventDefault()
              event.stopPropagation()
              const uid = event.dataTransfer.getData('text/member-uid')
              setDragHoverMeetingId(null)
              setDraggingMemberUid(null)
              if (uid) addParticipant(uid, bar.meeting.id)
            }}
          >
            <span className="meetingTimelineAvatars">
              {effective.map((uid) => {
                const isPhantom = draggingMemberUid === uid && dragHoverMeetingId === bar.meeting.id && !participantIds(bar.meeting).includes(uid) && !draft.participantIds.includes(uid)
                const isPending = !isPhantom && pending.has(uid)
                return <i key={uid} className={isPhantom ? 'isPhantom' : isPending ? 'isPending' : ''}>{initials(memberById.get(uid)?.name ?? 'Member')}</i>
              })}
            </span>
            <strong>{bar.meeting.title}</strong>
            <em>{meetingDate(bar.meeting.scheduledAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</em>
            {canManageMeetings && <span className="meetingTimelineResize" onPointerDown={(event) => resizeBar(event, bar.meeting)} />}
          </Pressable>
          )
        })}
        {draftBar ? (
          <Pressable
            className={`meetingTimelineBar meetingTimelineDraftBar isSelected${dragHoverMeetingId === DRAFT_MEETING_DROP_TARGET_ID ? ' isDropTarget' : ''}`}
            style={{ left: `${draftBar.left}%`, width: `${draftBar.width}%`, top: `${draftBar.top}px`, '--meeting-bar': 'var(--accent)' } as CSSProperties}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => { event.stopPropagation(); setCreateOpen(true); setSelectedId(null); if (isMobile) setDetailsSheetOpen(true) }}
            onDragOver={(event) => {
              if (!event.dataTransfer.types.includes('text/member-uid')) return
              event.preventDefault()
              event.dataTransfer.dropEffect = 'copy'
              if (dragHoverMeetingId !== DRAFT_MEETING_DROP_TARGET_ID) setDragHoverMeetingId(DRAFT_MEETING_DROP_TARGET_ID)
            }}
            onDragLeave={(event) => {
              const related = event.relatedTarget as Node | null
              if (related && event.currentTarget.contains(related)) return
              if (dragHoverMeetingId === DRAFT_MEETING_DROP_TARGET_ID) setDragHoverMeetingId(null)
            }}
            onDrop={(event) => {
              event.preventDefault()
              event.stopPropagation()
              const uid = event.dataTransfer.getData('text/member-uid')
              setDragHoverMeetingId(null)
              setDraggingMemberUid(null)
              if (uid) addParticipant(uid, DRAFT_MEETING_DROP_TARGET_ID)
            }}
          >
            <span className="meetingTimelineAvatars">
              {effectiveDraftParticipantIds.length > 0 ? effectiveDraftParticipantIds.slice(0, 3).map((uid) => {
                const isPhantom = isPhantomDraftAvatarFor(uid, participantContext)
                return <i key={uid} className={isPhantom ? 'isPhantom' : 'isPending'}>{initials(memberById.get(uid)?.name ?? 'Member')}</i>
              }) : <i>+</i>}
            </span>
            <strong>{draft.title || t('NLSMeetingsActionNewMeeting')}</strong>
            <em>{selectedStart.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</em>
          </Pressable>
        ) : null}
      </div>
    </section>
  )

  const huddleLivePanel: ReactNode = (
    <section className="meetingLivePanel">
      <div>
        <span className="settingsEyebrow">Live huddle</span>
        <h2>{linkedChannels[0] ? `#${linkedChannels[0].slug || linkedChannels[0].name}` : 'Workspace huddle'}</h2>
        <p>{listeningCount} {t('NLSMeetingsLabelListening')} · {speakingCount} {t('NLSMeetingsLabelSpeaking')} · presence-based for now</p>
      </div>
      <div className="meetingLiveControls">
        <Button variant={isSpeaking ? 'primary' : 'secondary'} icon={isSpeaking ? <MicOffRoundedIcon /> : <MicRoundedIcon />} onClick={toggleSpeaking}>{isSpeaking ? 'Stop speaking' : 'Speak'}</Button>
        <Button variant={isListening ? 'secondary' : 'primary'} icon={<HeadphonesRoundedIcon />} onClick={toggleListening}>{isListening ? 'Leave huddle' : 'Join huddle'}</Button>
        {linkedChannels[0] && onOpenChat ? <Button variant="tertiary" icon={<ChatBubbleOutlineRoundedIcon />} onClick={() => selected && openLinkedChat(selected)}>Open chat</Button> : null}
      </div>
      <div className="meetingLiveRoster">
        {(huddlePresence.length ? huddlePresence : []).map((presence) => (
          <article key={presence.uid}>
            <span>{initials(presence.displayName)}</span>
            <strong>{presence.displayName}</strong>
            <em>{presence.isSpeaking ? t('NLSMeetingsLabelSpeaking') : presence.isListening ? t('NLSMeetingsLabelListening') : t('NLSMeetingsLabelPresent')}</em>
          </article>
        ))}
        {huddlePresence.length === 0 ? <p>{t('NLSMeetingsEmptyNoHuddle')}</p> : null}
      </div>
    </section>
  )

  const meetingLivePanel: ReactNode = (
    <section className="meetingLivePanel">
      <div>
        <span className="settingsEyebrow">Live meeting</span>
        <h2>{selected?.title ?? 'Select or schedule a meeting'}</h2>
        <p>{selected ? `${meetingStatusLabel(selected.status)} · ${selected.sourceProvider.replace('_', ' ')}` : 'Zoom, Webex, and Google Meet are dummy provider surfaces until real integration is added.'}</p>
      </div>
      <div className="meetingLiveControls">
        {selected ? <Button variant="primary" icon={<OpenInNewRoundedIcon />} loading={joiningId === selected.id} disabled={!canJoinMeetings || joiningId === selected.id || ['CANCELLED', 'CONNECTION_INTERRUPTED'].includes(selected.status)} globalLoading={{ label: 'Opening meeting', messages: ['Checking the meeting link...', 'Opening the provider session...', 'Keeping repeated joins blocked...'] }} onClick={() => join(selected)}>{selected.sourceProvider === 'HUDDLE' ? t('NLSMeetingsActionJoinHuddleMeeting') : t('NLSMeetingsActionJoinMeeting')}</Button> : null}
        {canScheduleMeetings ? <Button variant="secondary" icon={<AddRoundedIcon />} onClick={openScheduleModal}>{t('NLSMeetingsActionNewMeeting')}</Button> : null}
      </div>
      <div className="meetingLiveRoster">
        {draft.participantIds.map((uid) => (
          <article key={uid}>
            <span>{initials(memberById.get(uid)?.name ?? 'Member')}</span>
            <strong>{memberById.get(uid)?.name ?? 'Workspace member'}</strong>
            <em>{memberById.get(uid)?.role ?? 'MEMBER'}</em>
          </article>
        ))}
        {draft.participantIds.length === 0 ? <p>Add members or a channel to set up this meeting.</p> : null}
      </div>
    </section>
  )

  const isCurrentMonthOnScreen = monthCursor.getFullYear() === new Date().getFullYear() && monthCursor.getMonth() === new Date().getMonth()

  const openCreateOnDay = (day: Date) => {
    if (!canScheduleMeetings) return
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0, 0, 0)
    requestOrRun(() => {
      setDraft({ ...draftFromMeeting(null, 'GOOGLE_MEET', prefillGoalId), title: 'New meeting', startInput: toLocalInput(start), durationMinutes: 30 })
      setCreateOpen(true)
      setSelectedId(null)
      if (isMobile) setDetailsSheetOpen(true)
    })
  }

  const monthGrid: ReactNode = (
    <div className="meetingMonthGrid" aria-label="Meetings calendar">
      <div className="meetingMonthWeekdays" aria-hidden="true">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => <span key={day}>{day}</span>)}
      </div>
      <div className="meetingMonthCells">
        {monthGridDays.map((day) => {
          const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`
          const inMonth = day.getMonth() === monthCursor.getMonth()
          const isToday = key === todayKey
          const dayMeetings = meetingsByDayKey.get(key) ?? []
          const mutableDayMeetings = dayMeetings.flatMap((entry) => entry.kind === 'meeting' ? [entry.meeting] : [])
          return (
            <div
              key={key}
              className={`meetingMonthCell${inMonth ? '' : ' isOutside'}${isToday ? ' isToday' : ''}`}
              role="gridcell"
              onDragOver={(event) => {
                if (event.dataTransfer.types.includes('text/member-uid')) event.preventDefault()
              }}
              onDrop={(event) => {
                event.preventDefault()
                const uid = event.dataTransfer.getData('text/member-uid')
                if (!uid) return
                if (mutableDayMeetings.length === 1) addParticipant(uid, mutableDayMeetings[0].id)
                else if (mutableDayMeetings.length === 0) showToast('Drop onto a meeting to add this person.', 'info')
                else showToast('Multiple meetings that day — drop onto the exact meeting pill.', 'info')
              }}
              onClick={(event) => {
                if ((event.target as HTMLElement).closest('.meetingMonthPill')) return
                openCreateOnDay(day)
              }}
            >
              <header>
                <span>{day.getDate()}</span>
                {isToday ? <em>Today</em> : null}
              </header>
              <div className="meetingMonthPills">
                {dayMeetings.map((entry) => entry.kind === 'meeting' ? (
                  <Pressable
                    key={entry.id}
                    className={`meetingMonthPill${selectedId === entry.meeting.id ? ' isSelected' : ''}${entry.meeting.status === 'CANCELLED' ? ' isCancelled' : ''}${dragHoverMeetingId === entry.meeting.id ? ' isDropTarget' : ''}`}
                    style={{ '--meeting-bar': meetingColorMap.get(entry.meeting.id) ?? COLORS[0] } as CSSProperties}
                    onClick={(event) => { event.stopPropagation(); selectMeeting(entry.meeting.id) }}
                    onDragOver={(event) => {
                      if (!event.dataTransfer.types.includes('text/member-uid')) return
                      event.preventDefault()
                      event.dataTransfer.dropEffect = 'copy'
                      if (dragHoverMeetingId !== entry.meeting.id) setDragHoverMeetingId(entry.meeting.id)
                    }}
                    onDragLeave={(event) => {
                      const related = event.relatedTarget as Node | null
                      if (related && event.currentTarget.contains(related)) return
                      if (dragHoverMeetingId === entry.meeting.id) setDragHoverMeetingId(null)
                    }}
                    onDrop={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      const uid = event.dataTransfer.getData('text/member-uid')
                      setDragHoverMeetingId(null)
                      setDraggingMemberUid(null)
                      if (uid) addParticipant(uid, entry.meeting.id)
                    }}
                  >
                    <span className="meetingMonthPillDot" aria-hidden="true" />
                    <em>{entry.startsAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</em>
                    <strong>{entry.title}</strong>
                  </Pressable>
                ) : (
                  <div
                    key={entry.id}
                    className="meetingMonthPill meetingMonthHuddlePill"
                    onClick={(event) => event.stopPropagation()}
                    onDragOver={(event) => event.stopPropagation()}
                    onDrop={(event) => event.stopPropagation()}
                  >
                    <SensorsRoundedIcon aria-hidden="true" />
                    <em>{entry.startsAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</em>
                    <strong>{entry.title}</strong>
                  </div>
                ))}
                {dayMeetings.length === 0 && inMonth && canScheduleMeetings ? (
                  <span className="meetingMonthCellHint">Tap to schedule</span>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )


  const minutesFromClientY = (clientY: number, target: HTMLElement) => {
    const rect = target.getBoundingClientRect()
    const ratio = (clientY - rect.top) / rect.height
    const raw = Math.max(0, Math.min(WEEK_TOTAL_MINUTES, ratio * WEEK_TOTAL_MINUTES))
    return Math.round(raw / WEEK_SLOT_MINUTES) * WEEK_SLOT_MINUTES
  }

  const openWeekCreate = (dayIdx: number, startMin: number, duration: number) => {
    if (!canScheduleMeetings) return
    const start = new Date(weekStart.getTime() + dayIdx * DAY_MS + startMin * 60_000)
    requestOrRun(() => {
      setDraft({ ...draftFromMeeting(null, 'GOOGLE_MEET', prefillGoalId), title: 'New meeting', startInput: toLocalInput(start), durationMinutes: clampDuration(duration) })
      setCreateOpen(true)
      setSelectedId(null)
      if (isMobile) setDetailsSheetOpen(true)
    })
  }

  const handleWeekColumnPointerDown = (event: ReactPointerEvent<HTMLDivElement>, dayIdx: number) => {
    if (!canScheduleMeetings || event.button !== 0) return
    if ((event.target as HTMLElement).closest('.meetingWeekBlock, .meetingWeekDraftBlock')) return
    const startMin = minutesFromClientY(event.clientY, event.currentTarget)
    weekSelectionRef.current = { pointerId: event.pointerId, startMin, currentMin: startMin, dayIdx }
    event.currentTarget.setPointerCapture?.(event.pointerId)
    setWeekDraftSelection({ dayIdx, startMin, endMin: startMin + WEEK_SLOT_MINUTES })
  }

  const handleWeekColumnPointerMove = (event: ReactPointerEvent<HTMLDivElement>, dayIdx: number) => {
    const selection = weekSelectionRef.current
    if (!selection || selection.pointerId !== event.pointerId || selection.dayIdx !== dayIdx) return
    const currentMin = minutesFromClientY(event.clientY, event.currentTarget)
    selection.currentMin = currentMin
    const startMin = Math.min(selection.startMin, currentMin)
    const endMin = Math.max(selection.startMin, currentMin)
    setWeekDraftSelection({ dayIdx, startMin, endMin: Math.max(endMin, startMin + WEEK_SLOT_MINUTES) })
  }

  const handleWeekColumnPointerUp = (event: ReactPointerEvent<HTMLDivElement>, dayIdx: number) => {
    const selection = weekSelectionRef.current
    if (!selection || selection.pointerId !== event.pointerId) return
    weekSelectionRef.current = null
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    setWeekDraftSelection(null)
    const startMin = Math.min(selection.startMin, selection.currentMin)
    const endMin = Math.max(selection.startMin, selection.currentMin)
    const duration = Math.max(WEEK_SLOT_MINUTES, endMin - startMin)
    openWeekCreate(dayIdx, startMin, duration || WEEK_SLOT_MINUTES)
  }

  const weekGrid: ReactNode = (
    <div className="meetingWeekBoard" aria-label="Meetings week calendar">
      <div className="meetingWeekHead" aria-hidden="false">
        <div className="meetingWeekHeadGutter" aria-hidden="true" />
        {weekDays.map((day) => {
          const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`
          const isToday = key === todayKey
          return (
            <div key={key} className={`meetingWeekHeadDay${isToday ? ' isToday' : ''}`}>
              <span className="meetingWeekHeadWeekday">{day.toLocaleDateString(locale, { weekday: 'short' }).toUpperCase()}</span>
              <span className="meetingWeekHeadDate">{day.getDate()}</span>
              {isToday ? <em>Today</em> : null}
            </div>
          )
        })}
      </div>
      <div className="meetingWeekBody" ref={weekBodyRef}>
        <div
          className="meetingWeekGrid"
          style={{ '--week-slot-h': `${WEEK_SLOT_HEIGHT_PX}px`, '--week-total-h': `${WEEK_TOTAL_HEIGHT_PX}px` } as CSSProperties}
        >
          <div className="meetingWeekHoursGutter" aria-hidden="true">
            {Array.from({ length: 24 }, (_, hour) => (
              <span key={hour} style={{ height: `${WEEK_SLOT_HEIGHT_PX * 2}px` }}>
                {hour === 0 ? '' : `${pad(hour)}:00`}
              </span>
            ))}
          </div>
          <div className="meetingWeekColumns">
            {weekDays.map((day, dayIdx) => {
              const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`
              const isToday = key === todayKey
              const blocks = weekMeetingsByDay.get(dayIdx) ?? []
              const selection = weekDraftSelection?.dayIdx === dayIdx ? weekDraftSelection : null
              const draftInThisColumn = weekDraftBlock?.dayIdx === dayIdx ? weekDraftBlock : null
              const nowInThisColumn = nowMarker?.dayIdx === dayIdx ? nowMarker : null
              return (
                <div
                  key={key}
                  className={`meetingWeekColumn${isToday ? ' isToday' : ''}`}
                  role="gridcell"
                  onPointerDown={(event) => handleWeekColumnPointerDown(event, dayIdx)}
                  onPointerMove={(event) => handleWeekColumnPointerMove(event, dayIdx)}
                  onPointerUp={(event) => handleWeekColumnPointerUp(event, dayIdx)}
                  onPointerCancel={() => { weekSelectionRef.current = null; setWeekDraftSelection(null) }}
                  onDragOver={(event) => {
                    if (event.dataTransfer.types.includes('text/member-uid')) event.preventDefault()
                  }}
                  onDrop={(event) => {
                    event.preventDefault()
                    const uid = event.dataTransfer.getData('text/member-uid')
                    if (uid) showToast('Drop onto a meeting to add this person.', 'info')
                  }}
                >
                  {nowInThisColumn ? (
                    <span
                      className="meetingWeekNow"
                      style={{ top: `${(nowInThisColumn.minutes / WEEK_TOTAL_MINUTES) * 100}%` }}
                      aria-hidden="true"
                    />
                  ) : null}
                  {selection ? (
                    <span
                      className="meetingWeekSelection"
                      style={{
                        top: `${(selection.startMin / WEEK_TOTAL_MINUTES) * 100}%`,
                        height: `${((selection.endMin - selection.startMin) / WEEK_TOTAL_MINUTES) * 100}%`,
                      }}
                      aria-hidden="true"
                    />
                  ) : null}
                  {blocks.map(({ entry, startMin, endMin }) => {
                    if (entry.kind === 'huddle') {
                      return (
                        <div
                          key={`${entry.id}-${dayIdx}`}
                          className="meetingWeekBlock meetingWeekHuddleBlock"
                          style={{
                            top: `${(startMin / WEEK_TOTAL_MINUTES) * 100}%`,
                            height: `${((endMin - startMin) / WEEK_TOTAL_MINUTES) * 100}%`,
                          }}
                          onPointerDown={(event) => event.stopPropagation()}
                          onDragOver={(event) => event.stopPropagation()}
                          onDrop={(event) => event.stopPropagation()}
                        >
                          <span className="meetingWeekBlockTime">
                            {entry.startsAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <strong className="meetingWeekBlockTitle"><SensorsRoundedIcon aria-hidden="true" />{entry.title}</strong>
                        </div>
                      )
                    }
                    const meeting = entry.meeting
                    const pending = getPendingParticipantIds(meeting)
                    const effective = getEffectiveParticipantIds(meeting).slice(0, 3)
                    return (
                    <Pressable
                      key={meeting.id}
                      className={`meetingWeekBlock${selectedId === meeting.id ? ' isSelected' : ''}${meeting.status === 'CANCELLED' ? ' isCancelled' : ''}${dragHoverMeetingId === meeting.id ? ' isDropTarget' : ''}`}
                      style={{
                        top: `${(startMin / WEEK_TOTAL_MINUTES) * 100}%`,
                        height: `${((endMin - startMin) / WEEK_TOTAL_MINUTES) * 100}%`,
                        '--meeting-bar': meetingColorMap.get(meeting.id) ?? COLORS[0],
                      } as CSSProperties}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => { event.stopPropagation(); selectMeeting(meeting.id) }}
                      onDragOver={(event) => {
                        if (!event.dataTransfer.types.includes('text/member-uid')) return
                        event.preventDefault()
                        event.dataTransfer.dropEffect = 'copy'
                        if (dragHoverMeetingId !== meeting.id) setDragHoverMeetingId(meeting.id)
                      }}
                      onDragLeave={(event) => {
                        const related = event.relatedTarget as Node | null
                        if (related && event.currentTarget.contains(related)) return
                        if (dragHoverMeetingId === meeting.id) setDragHoverMeetingId(null)
                      }}
                      onDrop={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        const uid = event.dataTransfer.getData('text/member-uid')
                        setDragHoverMeetingId(null)
                        setDraggingMemberUid(null)
                        if (uid) addParticipant(uid, meeting.id)
                      }}
                    >
                      <span className="meetingWeekBlockTime">
                        {meetingDate(meeting.scheduledAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <strong className="meetingWeekBlockTitle">{meeting.title}</strong>
                      {effective.length > 0 ? (
                        <span className="meetingWeekBlockAvatars">
                          {effective.map((uid) => {
                            const isPhantom = draggingMemberUid === uid && dragHoverMeetingId === meeting.id && !participantIds(meeting).includes(uid) && !draft.participantIds.includes(uid)
                            const isPending = !isPhantom && pending.has(uid)
                            return <i key={uid} className={isPhantom ? 'isPhantom' : isPending ? 'isPending' : ''}>{initials(memberById.get(uid)?.name ?? 'Member')}</i>
                          })}
                        </span>
                      ) : null}
                    </Pressable>
                    )
                  })}
                  {draftInThisColumn ? (
                    <Pressable
                      className={`meetingWeekBlock meetingWeekDraftBlock isSelected${dragHoverMeetingId === DRAFT_MEETING_DROP_TARGET_ID ? ' isDropTarget' : ''}`}
                      style={{
                        top: `${(draftInThisColumn.startMin / WEEK_TOTAL_MINUTES) * 100}%`,
                        height: `${((draftInThisColumn.endMin - draftInThisColumn.startMin) / WEEK_TOTAL_MINUTES) * 100}%`,
                        '--meeting-bar': 'var(--accent)',
                      } as CSSProperties}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => { event.stopPropagation(); setCreateOpen(true); setSelectedId(null); if (isMobile) setDetailsSheetOpen(true) }}
                      onDragOver={(event) => {
                        if (!event.dataTransfer.types.includes('text/member-uid')) return
                        event.preventDefault()
                        event.dataTransfer.dropEffect = 'copy'
                        if (dragHoverMeetingId !== DRAFT_MEETING_DROP_TARGET_ID) setDragHoverMeetingId(DRAFT_MEETING_DROP_TARGET_ID)
                      }}
                      onDragLeave={(event) => {
                        const related = event.relatedTarget as Node | null
                        if (related && event.currentTarget.contains(related)) return
                        if (dragHoverMeetingId === DRAFT_MEETING_DROP_TARGET_ID) setDragHoverMeetingId(null)
                      }}
                      onDrop={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        const uid = event.dataTransfer.getData('text/member-uid')
                        setDragHoverMeetingId(null)
                        setDraggingMemberUid(null)
                        if (uid) addParticipant(uid, DRAFT_MEETING_DROP_TARGET_ID)
                      }}
                    >
                      <span className="meetingWeekBlockTime">
                        {selectedStart.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <strong className="meetingWeekBlockTitle">{draft.title || t('NLSMeetingsActionNewMeeting')}</strong>
                      {effectiveDraftParticipantIds.length > 0 ? (
                        <span className="meetingWeekBlockAvatars">
                          {effectiveDraftParticipantIds.slice(0, 3).map((uid) => {
                            const isPhantom = isPhantomDraftAvatarFor(uid, participantContext)
                            return <i key={uid} className={isPhantom ? 'isPhantom' : 'isPending'}>{initials(memberById.get(uid)?.name ?? 'Member')}</i>
                          })}
                        </span>
                      ) : null}
                    </Pressable>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )

  const shiftScheduleView = (direction: -1 | 1) => {
    requestOrRun(() => setFocusedDate((current) => scheduleView === 'WEEK' ? addDays(current, direction * 7) : addMonths(current, direction)))
  }
  const jumpToToday = () => requestOrRun(() => setFocusedDate(new Date()))
  const changeScheduleView = (view: ScheduleView) => {
    if (view === scheduleView) return
    requestOrRun(() => setScheduleView(view))
  }

  const scheduleStage: ReactNode = (
    <section className={`meetingScheduleBoardCard meetingScheduleBoardCard--${scheduleView === 'WEEK' ? 'week' : 'month'}`} aria-label="Schedule board">
      <header className="meetingMonthHead">
        <div className="meetingMonthHeadNav">
          <IconButton
            variant="tertiary"
            size="sm"
            aria-label={`Previous ${scheduleView === 'WEEK' ? 'week' : 'month'}`}
            label={`Previous ${scheduleView === 'WEEK' ? 'week' : 'month'}`}
            icon={<ChevronLeftRoundedIcon />}
            onClick={() => shiftScheduleView(-1)}
          />
          <strong>{scheduleView === 'WEEK' ? weekLabel : monthLabel}</strong>
          <IconButton
            variant="tertiary"
            size="sm"
            aria-label={`Next ${scheduleView === 'WEEK' ? 'week' : 'month'}`}
            label={`Next ${scheduleView === 'WEEK' ? 'week' : 'month'}`}
            icon={<ChevronRightRoundedIcon />}
            onClick={() => shiftScheduleView(1)}
          />
        </div>
        {(scheduleView === 'WEEK' ? !isCurrentWeekOnScreen : !isCurrentMonthOnScreen) ? (
          <Button variant="tertiary" size="sm" onClick={jumpToToday}>{t('NLSMeetingsActionToday')}</Button>
        ) : (
          <small>{canEditDraft ? (scheduleView === 'WEEK' ? 'Drag on the grid to pick a duration. Drop members onto a meeting.' : 'Drag members onto a pill to add them.') : 'Read-only calendar.'}</small>
        )}
        {canManageMeetings ? <Button variant="tertiary" size="sm" loading={calendarSyncing} icon={<CalendarMonthRoundedIcon />} onClick={() => void pushVisibleWeekToCalendar()}>Push week</Button> : null}
      </header>
      {scheduleView === 'WEEK' ? weekGrid : monthGrid}
    </section>
  )

  const stageContent: ReactNode = (
    <>
      {(canScheduleMeetings || canManageMeetings) ? (
        <MeetingReadinessGate ready={meetingGateway.isReady} loading={meetingGateway.loading} onOpenLink={onOpenLink} />
      ) : null}
      {sectionMode === 'SCHEDULE' && scheduleStage}
      {sectionMode === 'HUDDLE' && huddleLivePanel}
      {sectionMode === 'MEETING' && meetingLivePanel}
    </>
  )

  const sidecarStats: ReactNode = (
    <section className="meetingSidecarStats" aria-label={t('NLSMeetingsLabelWorkspaceStats')}>
      <article><span>{t('NLSMeetingsLabelUpcoming')}</span><strong>{upcomingCount}</strong><em>{t('NLSMeetingsLabelFromToday')}</em></article>
      <article><span>{t('NLSMeetingsLabelCalendar')}</span><strong>{busySlots.length}</strong><em>{t('NLSMeetingsLabelBusySlots')}</em></article>
      <article><span>{t('NLSMeetingsLabelHuddle')}</span><strong>{listeningCount}</strong><em>{t('NLSMeetingsLabelListening')}</em></article>
    </section>
  )

  const sidecarHeader: ReactNode = (
    <header className="meetingSidecarHeader">
      <div className="meetingSidecarHeaderIdentity">
        <span className="settingsEyebrow">{t('NLSMeetingsLabelMeetingDetails')}</span>
        <h2>{draft.id ? draft.title || 'Untitled meeting' : createOpen ? t('NLSMeetingsActionNewMeeting') : 'Select a meeting'}</h2>
      </div>
      {selected && (
        <div className="meetingSidecarHeaderStatus">
          <GhostBadge tone={selected.status === 'CONNECTION_INTERRUPTED' ? 'critical' : 'accent'}>{meetingStatusLabel(selected.status)}</GhostBadge>
        </div>
      )}
    </header>
  )

  const canCancelMeeting = canManageMeetings && !['CANCELLED', 'COMPLETED'].includes(selected?.status ?? '')
  const canDeleteMeeting = canManageMeetings && ['CANCELLED', 'COMPLETED'].includes(selected?.status ?? '')
  const canTranscribeMeeting = canManageMeetings && selected?.status === 'COMPLETED'
  const isParticipant = canJoinMeetings && Boolean(selected && (selected.participants ?? []).some((participant) => !participant.external && participant.userId === user.uid))
  const rsvpStatus = selected ? (selected.participants ?? []).find((participant) => !participant.external && participant.userId === user.uid)?.rsvpStatus ?? 'PENDING' : 'PENDING'

  const sidecarActions: ReactNode = selected ? (
    <div className="meetingSidecarActions" role="group" aria-label={t('NLSMeetingsAriaActions')}>
      <div className="meetingSidecarActionsPrimaryRow">
        <Button
          variant="primary"
          size="md"
          icon={<OpenInNewRoundedIcon />}
          loading={joiningId === selected.id}
          disabled={!canJoinMeetings || joiningId === selected.id || ['CANCELLED', 'CONNECTION_INTERRUPTED'].includes(selected.status)}
          globalLoading={{ label: 'Opening meeting', messages: ['Checking the meeting link...', 'Opening the provider session...', 'Keeping repeated joins blocked...'] }}
          onClick={() => join(selected)}
          className="meetingSidecarActionsPrimary"
        >
          {joiningId === selected.id ? t('NLSMeetingsActionOpening') : selected.sourceProvider === 'HUDDLE' ? t('NLSMeetingsActionJoinHuddle') : t('NLSMeetingsActionJoin')}
        </Button>
        {canCancelMeeting ? (
          <Button
            variant="tertiary"
            tone="danger"
            size="md"
            className="meetingSidecarActionsCancel"
            disabled={saving === 'ALL'}
            onClick={() => void cancelSelectedMeeting()}
            icon={<CloseRoundedIcon />}
          >
            {saving === 'ALL' ? 'Cancelling…' : t('NLSMeetingsActionCancel')}
          </Button>
        ) : null}
      </div>

      <div className="meetingSidecarActionsGrid">
        {onOpenChat && (() => {
          const canJoinExistingChannel = Boolean(meetingChannel)
          const canCreateNewChannel = canCreateMeetingChannel && meetingChannelMemberIds.length >= 2
          const canOpenCustomChannel = roleCanPerform(activeRole, 'ADMIN') && canCreateCustomMeetingChannel && meetingChannelMemberIds.length >= 2
          const chatMenuHasOptions = canJoinExistingChannel || canCreateNewChannel || canOpenCustomChannel
          if (!chatMenuHasOptions) {
            return (
              <Pressable
                className="meetingSidecarActionsTile"
                onClick={() => requestOrRun(() => openLinkedChat(selected))}
              >
                <ChatBubbleOutlineRoundedIcon />
                <span>{t('NLSMeetingsActionChat')}</span>
              </Pressable>
            )
          }
          return (
            <div className="meetingSidecarActionsTileGroup" ref={chatMenuRef}>
              <Pressable
                className="meetingSidecarActionsTile"
                onClick={() => requestOrRun(() => openLinkedChat(selected))}
              >
                <ChatBubbleOutlineRoundedIcon />
                <span>{t('NLSMeetingsActionChat')}</span>
              </Pressable>
              <IconButton
                ref={chatCaretRef}
                variant="tertiary"
                size="sm"
                className="meetingSidecarActionsTileMenu"
                aria-label="Meeting chat options"
                label="Meeting chat options"
                aria-expanded={chatMenuOpen}
                onClick={() => setChatMenuOpen((current) => !current)}
                icon={<KeyboardArrowDownRoundedIcon />}
              />
              <FloatingMenu
                anchorRef={chatCaretRef}
                isOpen={chatMenuOpen}
                onClose={() => setChatMenuOpen(false)}
                placement="bottom-end"
                className="meetingChatDropdown"
                role="menu"
                ariaLabel="Meeting chat options"
              >
                {(canJoinExistingChannel || canCreateNewChannel) ? (
                  <Pressable
                    role="menuitem"
                    onClick={() => { setChatMenuOpen(false); requestOrRun(() => { void openMeetingChannel(selected) }) }}
                  >
                    {canJoinExistingChannel ? 'Join meeting channel' : 'Chat on new channel'}
                    <span>{canJoinExistingChannel ? `#${meetingChannel!.slug || meetingChannel!.name}` : `${Math.max(0, meetingChannelMemberIds.length)} members`}</span>
                  </Pressable>
                ) : null}
                {canOpenCustomChannel ? (
                  <Pressable
                    role="menuitem"
                    onClick={() => { setChatMenuOpen(false); requestOrRun(() => openCustomMeetingChannelDraft(selected)) }}
                  >
                    Chat on custom channel
                    <span>Edit members before creating</span>
                  </Pressable>
                ) : null}
              </FloatingMenu>
            </div>
          )
        })()}

        <div className="meetingSidecarActionsTileGroup">
          <Pressable
            className="meetingSidecarActionsTile"
            disabled={attachSaving}
            onClick={() => fileInputRef.current?.click()}
          >
            <FolderOpenRoundedIcon />
            <span>{t('NLSMeetingsActionDocs')}</span>
          </Pressable>
          <IconButton
            ref={docsCaretRef}
            variant="tertiary"
            size="sm"
            className="meetingSidecarActionsTileMenu"
            aria-label="Attach workspace item"
            label="Attach workspace item"
            aria-expanded={docsMenuOpen}
            onClick={() => setDocsMenuOpen((current) => !current)}
            icon={<KeyboardArrowDownRoundedIcon />}
          />
          <FloatingMenu
            anchorRef={docsCaretRef}
            isOpen={docsMenuOpen}
            onClose={() => setDocsMenuOpen(false)}
            placement="bottom-end"
            className="meetingChatDropdown"
            role="menu"
            ariaLabel="Docs options"
          >
            <Pressable
              role="menuitem"
              onClick={() => { setDocsMenuOpen(false); fileInputRef.current?.click() }}
            >
              Upload a file
              <span>Attach a document to this meeting</span>
            </Pressable>
            <Pressable
              role="menuitem"
              onClick={() => { setDocsMenuOpen(false); setDocsPickerOpen(true) }}
            >
              Attach workspace item
              <span>Link a goal, task, or checklist</span>
            </Pressable>
            <Pressable
              role="menuitem"
              onClick={() => { setDocsMenuOpen(false); setAssetsPickerOpen(true) }}
            >
              Attach files, forms, designs, links
              <span>Batch-attach workspace assets</span>
            </Pressable>
            <Pressable
              role="menuitem"
              onClick={() => { setDocsMenuOpen(false); requestOrRun(() => onOpenDocs(selected.id)) }}
            >
              Open docs hub
              <span>Browse every attached file</span>
            </Pressable>
          </FloatingMenu>
          <FloatingMenu
            anchorRef={docsCaretRef}
            isOpen={docsPickerOpen}
            onClose={() => setDocsPickerOpen(false)}
            placement="bottom-end"
            className="meetingWorkspacePickerMenu"
            role="dialog"
            ariaLabel="Pick a workspace item"
          >
            <WorkspaceItemPicker
              elements={elements}
              onSelect={(reference) => { void handleAttachEntity(reference) }}
              onClose={() => { setDocsPickerOpen(false); setDocsMenuOpen(true) }}
              disabledIds={new Set(workspaceItemAttachments.map((entry) => entry.refId))}
              ariaLabel="Pick a workspace item to attach"
            />
          </FloatingMenu>
          <FloatingMenu
            anchorRef={docsCaretRef}
            isOpen={assetsPickerOpen}
            onClose={() => setAssetsPickerOpen(false)}
            placement="bottom-end"
            className="meetingWorkspacePickerMenu"
            role="dialog"
            ariaLabel="Pick workspace assets to attach"
          >
            <WorkspaceItemPicker
              mode="itemAttachment"
              enabledKinds={['forms', 'designs', 'uploads', 'links']}
              multiSelect
              forms={formsPage.items}
              designs={designsPage.items}
              uploads={workspaceFiles.map((file) => ({
                id: file.id,
                name: file.fileName,
                title: file.fileName,
                sizeBytes: file.sizeBytes,
                mimeType: file.mimeType,
              }))}
              links={[]}
              onCommitRefs={(references) => { void handleAttachEntities(references) }}
              onClose={() => setAssetsPickerOpen(false)}
              disabledIds={new Set(workspaceItemAttachments.map((entry) => entry.refId))}
              ariaLabel="Pick workspace assets to attach"
            />
          </FloatingMenu>
          <HiddenFileInput
            ref={fileInputRef}
            onFiles={(files) => {
              const file = files[0]
              if (file) void handleAttachUpload(file)
            }}
          />
        </div>

        {canManageMeetings && (
          <Pressable
            className="meetingSidecarActionsTile"
            disabled={!canTranscribeMeeting}
            title={canTranscribeMeeting ? undefined : 'Available after the meeting ends'}
            onClick={() => void requestMeetingTranscription(selected.id).then(() => showToast('Transcription stub queued for 25 credits.', 'success')).catch((error) => showToast(error.message, 'error'))}
          >
            <DescriptionRoundedIcon />
            <span>{t('NLSMeetingsActionTranscribe')}</span>
          </Pressable>
        )}
      </div>

      <ExternalLinkSidecard
        item={{
          id: selected.id,
          type: 'MEETING',
          providerMappings: selected.gcalEventId ? {
            google_calendar: {
              provider: 'GOOGLE_CALENDAR',
              targets: {
                primary: { externalId: selected.gcalEventId, targetId: 'primary' },
              },
            },
          } : undefined,
        }}
        providers={['GOOGLE_CALENDAR']}
        mode="sidecar"
        disabled={calendarSyncing}
        onPush={canManageMeetings ? () => void pushSelectedMeetingToCalendar() : undefined}
        onPull={canManageMeetings ? () => void pushSelectedMeetingToCalendar() : undefined}
      />
      {selected.sourceProvider === 'ZOOM' && selected.providerMeetingId ? (
        <ExternalLinkSidecard
          item={{
            id: selected.id,
            type: 'MEETING',
            providerMappings: {
              zoom: {
                provider: 'ZOOM',
                targets: { primary: { externalId: selected.providerMeetingId, targetId: 'primary' } },
              },
            },
          }}
          providers={['ZOOM']}
          mode="sidecar"
          disabled
        />
      ) : null}

      {isParticipant ? (
        <div className="meetingSidecarActionsRsvp">
          <Select<MeetingRsvpStatus>
            trigger="labeledPill"
            label="RSVP"
            ariaLabel="Meeting RSVP status"
            value={rsvpStatus}
            options={[{ value: 'PENDING', label: 'Pending' }, { value: 'ACCEPTED', label: 'Accepted' }, { value: 'TENTATIVE', label: 'Tentative' }, { value: 'DECLINED', label: 'Declined' }]}
            onChange={(value) => void updateMeetingRsvp(selected.id, value)}
          />
        </div>
      ) : null}

      {canDeleteMeeting ? (
        <Button
          variant="tertiary"
          tone="danger"
          size="md"
          icon={<DeleteForeverRoundedIcon />}
          disabled={deleting}
          onClick={() => setDeleteConfirmOpen(true)}
        >
          {deleting ? 'Deleting…' : 'Delete meeting'}
        </Button>
      ) : null}
    </div>
  ) : null
  const activeDraftDropTargetId = createOpen && !draft.id ? DRAFT_MEETING_DROP_TARGET_ID : selected?.id ?? null
  const isHoveringActiveDraft = Boolean(
    draggingMemberUid &&
    activeDraftDropTargetId &&
    dragHoverMeetingId === activeDraftDropTargetId &&
    !draft.participantIds.includes(draggingMemberUid),
  )

  const attachmentsSection: ReactNode = selected && (workspaceItemAttachments.length + uploadAttachments.length > 0) ? (
    <Collapse
      className="meetingSidecarCollapse"
      defaultOpen
      label={<span className="settingsEyebrow">Attachments</span>}
      aside={<GhostBadge tone="muted">{workspaceItemAttachments.length + uploadAttachments.length}</GhostBadge>}
    >
      <div className="meetingAttachmentsList">
        {workspaceItemAttachments.map((attachment) => (
          <TagChip
            key={`ent-${attachment.refId}`}
            className="meetingAttachmentChip--entity"
            onRemove={canManageMeetings ? () => void handleDetachAttachment(attachments.indexOf(attachment)) : undefined}
            removeLabel={`Remove ${attachment.refLabel}`}
          >
            <em className="meetingAttachmentChipTag">For</em>
            {attachment.refLabel}
          </TagChip>
        ))}
        {uploadAttachments.map((attachment) => (
          <TagChip
            key={`up-${attachment.fileId}`}
            className="meetingAttachmentChip--upload"
            onRemove={canManageMeetings ? () => void handleDetachAttachment(attachments.indexOf(attachment)) : undefined}
            removeLabel={`Remove ${attachment.fileName}`}
          >
            <em className="meetingAttachmentChipTag">Attachments</em>
            {attachment.fileName}
          </TagChip>
        ))}
      </div>
    </Collapse>
  ) : null

  const sidecarForm: ReactNode = (selected || createOpen) ? (
    <>
      <section className="meetingSidecarStatic">
        <header className="meetingSidecarStaticHead">
          <span className="settingsEyebrow">Title & description</span>
        </header>
        <div className="meetingSidecarStaticBody">
          <label>Title<TextInput value={draft.title} disabled={!canEditDraft} onChange={(title) => setDraft({ ...draft, title })} /></label>
          <label>Description<Textarea value={draft.description} disabled={!canEditDraft} onChange={(description) => setDraft({ ...draft, description })} /></label>
          <LinkedGoalPicker
            value={draft.scopeNodeId ?? null}
            disabled={!canEditDraft}
            onChange={(scopeNodeId) => setDraft({ ...draft, scopeNodeId })}
          />
          {!selected && canScheduleMeetings && <label>Provider<Select<MeetingProvider> trigger="pill" ariaLabel="Meeting provider" value={draft.sourceProvider} options={[{ value: 'GOOGLE_MEET', label: 'Google Meet' }, { value: 'ZOOM', label: 'Zoom' }, { value: 'WEBEX', label: 'Webex' }, { value: 'HUDDLE', label: 'Huddle only' }]} onChange={(value) => setDraft({ ...draft, sourceProvider: value })} /></label>}
        </div>
      </section>
      <section className="meetingSidecarStatic">
        <header className="meetingSidecarStaticHead">
          <span className="settingsEyebrow">When</span>
        </header>
        <div className="meetingSidecarStaticBody">
          <MeetingWhenPicker
            startInput={draft.startInput}
            durationMinutes={draft.durationMinutes}
            disabled={!canEditDraft}
            onChange={(next) => setDraft({ ...draft, startInput: next.startInput, durationMinutes: clampDuration(next.durationMinutes) })}
          />
        </div>
      </section>
      <section data-orbit-target="agenda-item" data-agenda-id={selected?.agendaNodes?.[0]?.nodeId ?? selected?.id} data-orbit-title={selected?.agendaNodes?.[0]?.nodeId ?? selected?.title} className="meetingReadiness"><div style={{ '--meeting-score': `${selectedReadiness.score || readiness[selected?.id ?? ''] || 0}%` } as CSSProperties}><strong>{selectedReadiness.score || readiness[selected?.id ?? ''] || 0}%</strong><span>{t('NLSMeetingsLabelReady')}</span></div><p>{selectedReadiness.total ? selectedReadiness.ready : 0}/{selectedReadiness.total || 0} agenda nodes actionable</p></section>
      {attachmentsSection}
      <Collapse
        className="meetingSidecarCollapse"
        defaultOpen
        label={<span className="settingsEyebrow">{t('NLSMeetingsLabelParticipants')}</span>}
        aside={<GhostBadge tone="muted">{draft.participantIds.length}</GhostBadge>}
      >
        <div className="meetingParticipantList">
          {draft.participantIds.length === 0 && !isHoveringActiveDraft ? <p className="meetingRosterEmpty">No one added yet. {isMobile ? 'Tap People to add members.' : 'Drag a member onto this meeting.'}</p> : null}
          {draggingMemberUid && isHoveringActiveDraft ? (() => {
            const phantomMember = memberById.get(draggingMemberUid)
            return (
              <article key={`phantom-${draggingMemberUid}`} className="isPhantom" aria-hidden="true">
                <span>{initials(phantomMember?.name ?? 'Member')}</span>
                <strong>{phantomMember?.name ?? 'Workspace member'}</strong>
                <em>{phantomMember?.role ?? 'MEMBER'}</em>
              </article>
            )
          })() : null}
          {draft.participantIds.map((uid) => {
            const member = memberById.get(uid)
            const savedIds = participantIds(selected)
            const isPending = Boolean(selected) && !savedIds.includes(uid)
            const rsvp = !isPending ? participantRsvpStatus(selected, uid) : null
            return (
              <article key={uid} data-orbit-target="attendee-chip" data-attendee-id={uid} data-orbit-title={member?.name ?? 'Workspace member'} className={isPending ? 'isPending' : ''}>
                <span>{initials(member?.name ?? 'Member')}</span>
                <strong>{member?.name ?? 'Workspace member'}</strong>
                <em>{member?.role ?? 'MEMBER'}</em>
                {rsvp && (
                  <GhostBadge tone={rsvp === 'ACCEPTED' ? 'success' : rsvp === 'TENTATIVE' ? 'warning' : rsvp === 'DECLINED' ? 'critical' : 'muted'}>
                    {t(
                      rsvp === 'ACCEPTED'
                        ? 'NLSMeetingsRsvpAccepted'
                        : rsvp === 'TENTATIVE'
                          ? 'NLSMeetingsRsvpTentative'
                          : rsvp === 'DECLINED'
                            ? 'NLSMeetingsRsvpDeclined'
                            : 'NLSMeetingsRsvpPending',
                    )}
                  </GhostBadge>
                )}
                {canEditDraft && (
                  <IconButton
                    variant="tertiary"
                    size="sm"
                    label={`Remove ${member?.name ?? 'member'}`}
                    icon={<CloseRoundedIcon />}
                    onClick={() => setDraft({ ...draft, participantIds: draft.participantIds.filter((id) => id !== uid) })}
                  />
                )}
              </article>
            )
          })}
        </div>
      </Collapse>
      <Collapse
        className="meetingSidecarCollapse"
        defaultOpen
        label={<span className="settingsEyebrow">{t('NLSMeetingsLabelLinkedChannels')}</span>}
        aside={<GhostBadge tone="muted">{linkedChannels.length}</GhostBadge>}
      >
        <p className="meetingLinkedChannelsHint">Linking a channel invites all its members to this meeting.</p>
        <div className="meetingLinkedChannels">
          {linkedChannels.map((channel) => (
            <Pressable key={channel.id} disabled={!canEditDraft} onClick={() => removeChannelFromDraft(channel.id)}>
              #{channel.slug || channel.name}
              {canEditDraft ? <CloseRoundedIcon /> : null}
            </Pressable>
          ))}
          {linkedChannels.length === 0 ? <span>No channel linked.</span> : null}
        </div>
      </Collapse>
    </>
  ) : (
    <div className="meetingSidecarEmpty"><VideoCameraFrontRoundedIcon /><strong>{t('NLSMeetingsEmptyNoMeetingSelectedTitle')}</strong><span>{t('NLSMeetingsEmptyNoMeetingSelectedBody')}</span></div>
  )

  const sidecarContent: ReactNode = (
    <>
      {sidecarStats}
      <section data-orbit-target="action-item" data-action-id={selected?.id} data-orbit-title={selected?.title} className="meetingSidecarDetails">
        {sidecarHeader}
        {sidecarActions}
        {sidecarForm}
      </section>
    </>
  )

  const pendingActions: PendingChangesDockAction[] = createOpen
    ? [{
        key: 'create',
        label: t('NLSMeetingsActionSaveMeeting'),
        variant: 'primary',
        loading: saving === 'CREATE',
        disabled: !draft.title.trim(),
        globalLoading: { label: 'Saving meeting', messages: ['Validating the meeting details...', 'Saving the meeting schedule...', 'Sending workspace updates...'] },
        onClick: () => { void saveCreate() },
      }]
    : (selected && canManageMeetings ? [
        // Silent save is only offered when no NEW participants were added.
        // Server enforces the same rule — see updateMeetingDetailsCore.
        ...(addedParticipantIds.length === 0 ? [{
          key: 'update-silent',
          label: 'Save silently',
          variant: 'tertiary' as const,
          loading: saving === 'NONE',
          globalLoading: { label: 'Saving meeting', messages: ['Applying quiet updates...', 'Persisting changes...', 'Skipping participant notifications...'] },
          onClick: () => { void saveExisting('NONE') },
        }] : []),
        ...(addedParticipantIds.length > 0 ? [{
          key: 'update-new',
          label: t('NLSMeetingsActionUpdateNew'),
          variant: 'secondary' as const,
          loading: saving === 'CHANGED',
          globalLoading: { label: 'Updating meeting', messages: ['Preparing participant updates...', 'Saving meeting changes...', 'Notifying the new participants...'] },
          onClick: () => { void saveExisting('CHANGED') },
        }] : []),
        {
          key: 'update-all',
          label: t('NLSMeetingsActionUpdateAll'),
          variant: 'primary' as const,
          loading: saving === 'ALL',
          globalLoading: { label: 'Updating meeting', messages: ['Preparing participant updates...', 'Saving meeting changes...', 'Notifying the workspace...'] },
          onClick: () => { void saveExisting('ALL') },
        },
      ] : [])

  const discardDraft = () => {
    setCreateOpen(false)
    setDraftSelection(null)
    setDraft(draftFromMeeting(selected))
  }

  const saveDock: ReactNode = (dirty || createOpen) ? (
    <PendingChangesDock
      open
      className="meetingLayoutSaveDock"
      title={createOpen ? t('NLSMeetingsActionSaveMeeting') : t('NLSMeetingsActionSaveSendUpdates')}
      summary={`${addedParticipantIds.length} added · ${removedParticipantIds.length} removed`}
      actions={pendingActions}
      onDiscard={discardDraft}
      discardLabel={t('NLSMeetingsActionDiscard')}
      ariaLabel={createOpen ? t('NLSMeetingsActionSaveMeeting') : t('NLSMeetingsActionSaveSendUpdates')}
    />
  ) : null

  const discardDialog: ReactNode = (
    <AppDialog
      open={Boolean(pendingAction)}
      title={meetingDirtyDialog.title}
      message={meetingDirtyDialog.message}
      onDismiss={() => setPendingAction(null)}
      actions={[
        { label: meetingDirtyDialog.cancelLabel, variant: 'ghost', onClick: () => setPendingAction(null) },
        { label: meetingDirtyDialog.discardLabel, variant: 'danger', onClick: () => { const action = pendingAction; setPendingAction(null); setCreateOpen(false); setDraftSelection(null); setDraft(draftFromMeeting(selected)); action?.() } },
      ]}
    />
  )

  const deleteDialog: ReactNode = selected ? (
    <AppDialog
      open={deleteConfirmOpen}
      title="Delete this meeting?"
      message={`"${selected.title || 'Untitled meeting'}" will be permanently removed for everyone. This also clears any linked Google Calendar event. This cannot be undone.`}
      onDismiss={() => (!deleting && setDeleteConfirmOpen(false))}
      actions={[
        { label: 'Keep meeting', variant: 'ghost', onClick: () => setDeleteConfirmOpen(false), disabled: deleting },
        { label: deleting ? 'Deleting…' : 'Delete permanently', variant: 'danger', onClick: deleteSelectedMeeting, disabled: deleting, loading: deleting },
      ]}
    />
  ) : null

  if (isMobile) {
    const showLiveBar = huddlePresence.length > 0
    const detailsSheetTitle = createOpen ? t('NLSMeetingsActionScheduleMeeting') : selected ? draft.title || t('NLSMeetingsLabelMeetingDetails') : t('NLSMeetingsLabelMeetingDetails')
    return (
      <section data-orbit-target="canvas" className={`meetingLayout meetingLayout--mobile${!canScheduleMeetings ? ' meetingLayout--viewOnly' : ''}`} aria-label={t('NLSMeetingsAriaScheduler')}>
        <header className="meetingHubHeader--mobile">
          <div>
            <span className="settingsEyebrow">{t('NLSMeetingsIdentityTitle')}</span>
            <h1>{rangeTitle}</h1>
            <p>{workspace.name} · {formatMeetingCount(meetings.length, t)}</p>
          </div>
          <div className="meetingMobileStatRow">
            <span><strong>{busySlots.length}</strong><em>calendar busy</em></span>
            <span><strong>{listeningCount}</strong><em>in huddle</em></span>
          </div>
        </header>

        <RailChipTabs<SectionMode>
          orientation="horizontal"
          ariaLabel={t('NLSMeetingsAriaMode')}
          value={sectionMode}
          onChange={(key) => requestOrRun(() => setSectionMode(key as SectionMode))}
          items={meetingSectionLabels.map((section) => ({ key: section.id, label: section.label }))}
        />

        {sectionMode === 'SCHEDULE' && (
          <div className="meetingMobileDateNav" role="group" aria-label="Timeline navigation">
            <IconButton
              variant="tertiary"
              size="sm"
              icon={<ChevronLeftRoundedIcon />}
              label={`Previous ${viewMode.toLowerCase()}`}
              onClick={() => requestOrRun(() => shiftFocus(-1))}
            />
            <Button variant="secondary" size="sm" onClick={jumpToToday}>{t('NLSMeetingsActionToday')}</Button>
            <label className="meetingHeaderSelect">
              <span>Range</span>
              <SheetSelect<ViewMode>
                ariaLabel="Meeting range"
                value={viewMode}
                options={meetingViewLabels.map((view) => ({ value: view.id, label: view.label }))}
                onChange={(value) => requestOrRun(() => setViewMode(value))}
              />
            </label>
            <IconButton
              variant="tertiary"
              size="sm"
              icon={<ChevronRightRoundedIcon />}
              label={`Next ${viewMode.toLowerCase()}`}
              onClick={() => requestOrRun(() => shiftFocus(1))}
            />
          </div>
        )}

        {(canScheduleMeetings || canManageMeetings) ? (
          <MeetingReadinessGate ready={meetingGateway.isReady} loading={meetingGateway.loading} onOpenLink={onOpenLink} />
        ) : null}

        <div className="meetingMobileStage">
          {sectionMode === 'SCHEDULE' && timelineBoard}
          {sectionMode === 'HUDDLE' && huddleLivePanel}
          {sectionMode === 'MEETING' && meetingLivePanel}
        </div>

        {showLiveBar ? (
          <Pressable
            className="meetingMobileLiveBar"
            onClick={() => setSectionMode('HUDDLE')}
            aria-label="Open live huddle"
          >
            <span className="meetingMobileLiveBarPulse" aria-hidden="true" />
            <strong>Live · {listeningCount} in huddle</strong>
            <em>{speakingCount} speaking</em>
          </Pressable>
        ) : null}

        <div className="meetingMobileActionBar">
          <Button variant="secondary" size={isMobile ? 'lg' : 'sm'} icon={<PeopleAltRoundedIcon />} onClick={() => setRosterSheetOpen(true)}>{t('NLSMeetingsActionPeople')}</Button>
          {canScheduleMeetings && (
            <Button variant="primary" size={isMobile ? 'lg' : 'sm'} icon={<AddRoundedIcon />} onClick={openScheduleModal}>{t('NLSMeetingsActionSchedule')}</Button>
          )}
          {(selected || createOpen) && (
            <Button variant="secondary" size={isMobile ? 'lg' : 'sm'} icon={<EventNoteRoundedIcon />} onClick={() => setDetailsSheetOpen(true)}>{t('NLSMeetingsActionDetails')}</Button>
          )}
        </div>

        <BottomSheet
          isOpen={rosterSheetOpen}
          onClose={() => setRosterSheetOpen(false)}
          title="Members & channels"
          className="meetingMobileSheet meetingMobileSheet--roster"
        >
          <div className="meetingMobileSheetBody">{rosterContent}</div>
        </BottomSheet>

        <BottomSheet
          isOpen={detailsSheetOpen}
          onClose={() => setDetailsSheetOpen(false)}
          title={detailsSheetTitle}
          className="meetingMobileSheet meetingMobileSheet--details"
        >
          <div className="meetingMobileSheetBody">{sidecarContent}</div>
        </BottomSheet>

        {saveDock}
        {discardDialog}
        {deleteDialog}
      </section>
    )
  }

  const sectionActiveMeta = getMeetingSectionMeta(sectionMode, t)
  const meetingsMiddleHeaderActions = (
    <>
      {sectionMode === 'SCHEDULE' && (
        <>
          <RailChipTabs<ScheduleView>
            orientation="horizontal"
            ariaLabel="Calendar range"
            value={scheduleView}
            onChange={(key) => changeScheduleView(key as ScheduleView)}
            items={[
              { key: 'MONTH', label: 'Month' },
              { key: 'WEEK', label: 'Week' },
            ]}
          />
          <IconButton
            variant="tertiary"
            size="sm"
            pressed={showHuddles}
            icon={<SensorsRoundedIcon aria-hidden="true" />}
            label={showHuddles ? t('NLSMeetingsActionHideHuddles') : t('NLSMeetingsActionShowHuddles')}
            title={showHuddles ? t('NLSMeetingsActionHideHuddles') : t('NLSMeetingsActionShowHuddles')}
            onClick={() => setShowHuddles((current) => !current)}
          />
        </>
      )}
      {canScheduleMeetings && sectionMode === 'SCHEDULE' && <Button variant="primary" icon={<AddRoundedIcon />} onClick={openScheduleModal}>{t('NLSMeetingsActionScheduleMeeting')}</Button>}
      {sectionMode === 'HUDDLE' && <Button variant="primary" icon={<AddRoundedIcon />} onClick={openScheduleModal}>{t('NLSMeetingsActionStartHuddle')}</Button>}
      {sectionMode === 'MEETING' && <Button variant="primary" icon={<AddRoundedIcon />} onClick={openScheduleModal}>{t('NLSMeetingsActionJoinRoom')}</Button>}
    </>
  )
  return (
    <>
    <RailPageShell
      variant="three-column"
      ariaLabel={t('NLSMeetingsAriaWorkspace')}
      data-orbit-target="canvas"
      className={`meetingLayout meetingLayout--hub meetingLayout--${variant}${!canScheduleMeetings ? ' meetingLayout--viewOnly' : ''}`}
      identity={{ badge: 'M', title: t('NLSMeetingsIdentityTitle'), subtitle: t('NLSMeetingsIdentitySubtitle') }}
      leftRailList={(
        <RailChipTabs<SectionMode>
          ariaLabel={t('NLSMeetingsAriaMode')}
          value={sectionMode}
          onChange={(next) => requestOrRun(() => setSectionMode(next))}
          items={SECTION_META.map<RailChipTabItem<SectionMode>>((item) => {
            const meta = getMeetingSectionMeta(item.id, t)
            return ({
            key: item.id,
            label: meta.label,
            subtitle: meta.tone,
            icon: item.icon,
            })
          })}
        />
      )}
      leftRailExtras={(
        <div ref={meetingsRailScroll.ref} {...meetingsRailScroll.scrollProps} className="meetingRailScroll edgeFadeScroll">
          {railPeoplePanel}
        </div>
      )}
      leftRailBottom={(
        <footer className="meetingRailFooter">
          <span className={`meetingStatusDot${listeningCount > 0 ? ' isOnline' : ''}`} />
          {listeningCount > 0 ? `${listeningCount} in huddle` : 'Huddle idle'}
        </footer>
      )}
      middleHeader={{
        title: t('NLSMeetingsLabelMeetingHub'),
        subtitle: sectionActiveMeta.tone,
        actions: meetingsMiddleHeaderActions,
      }}
      middleStage={(
        <main className={`meetingStage meetingStage--hub${scheduleView === 'WEEK' && sectionMode === 'SCHEDULE' ? ' meetingStage--week' : ''}`}>
          {stageContent}
          {saveDock}
        </main>
      )}
      rightSidecar={(
        <aside className="meetingSidecar">
          {sidecarContent}
        </aside>
      )}
    />

      {discardDialog}
        {deleteDialog}
    </>
  )
}
